const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";
const TOKEN_KEY = "emc.token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The response's `detail` as it came, for a page that reads more than the
     * sentence — the settings form maps `detail.errors` onto its fields. */
    readonly detail: unknown = null,
  ) {
    super(message);
  }
}

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Private windows and blocked site data: the session simply lasts until
    // the tab is closed.
  }
}

/** The API's own sentence out of a FastAPI error body, whatever its shape: a
 * string (our routers), a list (FastAPI's validation errors: where, and
 * what), or an object with a `message`. Null when there is none, and the
 * status line stands. Without this a 422 from validation read "[object
 * Object]" on screen instead of what was wrong. */
export function detailText(detail: unknown): string | null {
  if (typeof detail === "string") return detail.trim() === "" ? null : detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item: unknown) => {
      if (item !== null && typeof item === "object" && "msg" in item) {
        const { loc, msg } = item as { loc?: unknown; msg: unknown };
        const where = Array.isArray(loc) ? loc.filter((part) => part !== "body").join(".") : "";
        return where ? `${where}: ${String(msg)}` : String(msg);
      }
      return typeof item === "string" ? item : JSON.stringify(item);
    });
    return parts.length > 0 ? parts.join("; ") : null;
  }
  if (detail !== null && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    return typeof message === "string" ? message : JSON.stringify(detail);
  }
  return null;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = readToken();
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    let raw: unknown = null;
    try {
      const body = (await response.json()) as { detail?: unknown };
      raw = body.detail ?? null;
      detail = detailText(body.detail) ?? detail;
    } catch {
      // Not JSON — keep the status line.
    }
    throw new ApiError(response.status, detail, raw);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** The digest as a page, fetched with the session's token.
 *
 * Not a plain link: the API takes the token in an `Authorization` header and a
 * new tab carries no headers, so the HTML is fetched here and handed to the
 * tab as a blob. The endpoint writes nothing — looking at a digest is not
 * sending one.
 */
export async function fetchDigestHtml(
  day: string,
  letter: DigestLetter = "owner",
  days = 7,
): Promise<string> {
  const token = readToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const query = new URLSearchParams({ day, letter, days: String(days) });
  const response = await fetch(`${BASE}/digest/daily?${query.toString()}`, { headers });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      detail = detailText(body.detail) ?? detail;
    } catch {
      // Not JSON — keep the status line.
    }
    throw new ApiError(response.status, detail);
  }
  return await response.text();
}

// --- Beállítások → Jelentések (2.7b): who gets which morning letter ---------------

export type DigestLetter = "owner" | "management";
export type DigestFrequency = "daily" | "weekly";

export interface DigestRecipient {
  id: number;
  email: string;
  letter: DigestLetter;
  frequency: DigestFrequency;
  /** 0 = Monday … 6 = Sunday; weekly only. */
  weekday: number | null;
  /** Every section of the letter: on or off. */
  sections: Record<string, boolean>;
  is_active: boolean;
  created_by: string;
  updated_by: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface DigestRecipientEvent {
  id: number;
  recipient_id: number;
  email: string;
  action: string;
  actor: string;
  detail: Record<string, unknown>;
  occurred_at: string | null;
}

export interface DigestLetterSpec {
  letter: DigestLetter;
  label: string;
  sections: { key: string; label: string }[];
}

export interface DigestRecipients {
  recipients: DigestRecipient[];
  letters: DigestLetterSpec[];
  /** While the list is empty, the owner's letter goes to these, daily. */
  fallback: { active: boolean; source: string; emails: string[] };
  events: DigestRecipientEvent[];
}

export interface DigestRecipientValues {
  email?: string;
  letter?: DigestLetter;
  frequency?: DigestFrequency;
  weekday?: number | null;
  sections?: Record<string, boolean>;
  is_active?: boolean;
}

export function fetchDigestRecipients(): Promise<DigestRecipients> {
  return request<DigestRecipients>("/digest/recipients");
}

export function addDigestRecipient(values: DigestRecipientValues): Promise<DigestRecipient> {
  return request<DigestRecipient>("/digest/recipients", { method: "POST", body: JSON.stringify(values) });
}

export function changeDigestRecipient(id: number, values: DigestRecipientValues): Promise<DigestRecipient> {
  return request<DigestRecipient>(`/digest/recipients/${id}`, { method: "PATCH", body: JSON.stringify(values) });
}

export function removeDigestRecipient(id: number): Promise<void> {
  return request<void>(`/digest/recipients/${id}`, { method: "DELETE" });
}

export type Role = "admin" | "marketer";

export type UserState = "invited" | "active" | "expired" | "deactivated";

export interface User {
  id: number;
  email: string;
  full_name: string;
  role: Role;
  /** 4.5: where the user is in its life; only `active` can sign in. */
  state?: UserState;
  is_active: boolean;
  last_login_at: string | null;
}

export async function login(email: string, password: string): Promise<void> {
  const token = await request<{ access_token: string }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  writeToken(token.access_token);
}

export function fetchMe(): Promise<User> {
  return request<User>("/auth/me");
}

// --- Users: Beállítások › Felhasználók and Profilom (slice 4.5) -------------

export type LinkPurpose = "invite" | "reset";

/** A set-password link as its issuer sees it — once. The token is stored
 * nowhere; `passwordLinkUrl` builds the address the person opens. */
export interface IssuedLink {
  token: string;
  purpose: LinkPurpose;
  expires_at: string;
}

export interface UserRow {
  id: number;
  email: string;
  full_name: string;
  role: Role;
  state: UserState;
  last_login_at: string | null;
  password_set_at: string | null;
  created_at: string;
  /** A link still waiting to be used: what for and until when. */
  link: { purpose: LinkPurpose; expires_at: string } | null;
}

/** What became of the link's mail (system mail, `emc_db.system_mail`). */
export type MailOutcome = "sent" | "logged" | "failed" | "unknown";

export interface LinkMail {
  outcome: MailOutcome;
  /** `ses` or `log`; null when the setting names neither. */
  transport: string | null;
  /** Why it did not go out, in the provider's or the settings' words. */
  detail: string | null;
}

export interface UserWithLink {
  user: UserRow;
  link: IssuedLink | null;
  /** Null when no link was issued. */
  mail: LinkMail | null;
}

/** One line of a user's log: who, when, what, old → new. */
export interface UserEvent {
  action: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  detail: Record<string, unknown>;
  occurred_at: string;
}

// --- Shopify shops (7.1): Beállítások → Webshopok ----------------------------------

export type ShopifyTokenState = "valid" | "failing" | "expired" | "none";

export interface ShopifyBackfillProgress {
  state: "running" | "paused" | "done" | "failed";
  lines_done: number;
  object_count: number | null;
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

export interface ShopifyStoreLine {
  handle: string;
  domain: string;
  country: string;
  configured: boolean;
  token_state: ShopifyTokenState;
  token_expires_at: string | null;
  token_refreshed_at: string | null;
  token_failures: number;
  token_error: string | null;
  webhooks_wanted: number;
  webhooks_registered: number;
  webhooks_missing: string[];
  webhooks_extra: string[];
  webhooks_checked_at: string | null;
  webhooks_error: string | null;
  reregister_requested_at: string | null;
  uninstalled_at: string | null;
  last_event_at: string | null;
  events_today: number;
  inbox_open: number;
  inbox_failed: number;
  backfill: Record<string, ShopifyBackfillProgress>;
}

export interface ShopifyStores {
  api_version: string;
  writeback_enabled: boolean;
  problems: string[];
  stores: ShopifyStoreLine[];
}

export function fetchShopifyStores(): Promise<ShopifyStores> {
  return request<ShopifyStores>("/shopify/stores");
}

export function reregisterShopifyWebhooks(handle: string): Promise<{ handle: string; requested_at: string }> {
  return request(`/shopify/stores/${encodeURIComponent(handle)}/reregister`, { method: "POST" });
}

export function fetchUsers(): Promise<UserRow[]> {
  return request<UserRow[]>("/users");
}

export function inviteUser(payload: { email: string; full_name: string; role: Role }): Promise<UserWithLink> {
  return request<UserWithLink>("/users", { method: "POST", body: JSON.stringify(payload) });
}

export function changeUserRole(id: number, role: Role): Promise<UserRow> {
  return request<UserRow>(`/users/${id}/role`, { method: "PATCH", body: JSON.stringify({ role }) });
}

export function deactivateUser(id: number): Promise<UserRow> {
  return request<UserRow>(`/users/${id}/deactivate`, { method: "POST" });
}

export function reactivateUser(id: number): Promise<UserWithLink> {
  return request<UserWithLink>(`/users/${id}/reactivate`, { method: "POST" });
}

export function issueUserLink(id: number): Promise<UserWithLink> {
  return request<UserWithLink>(`/users/${id}/link`, { method: "POST" });
}

export function fetchUserEvents(id: number): Promise<UserEvent[]> {
  return request<UserEvent[]>(`/users/${id}/events?limit=20`);
}

export function updateProfile(full_name: string): Promise<User> {
  return request<User>("/auth/me", { method: "PATCH", body: JSON.stringify({ full_name }) });
}

/** A new own password. Every other session ends; this one continues on the
 * token the answer carries. */
export async function changeOwnPassword(current_password: string, new_password: string): Promise<void> {
  const token = await request<{ access_token: string }>("/auth/me/password", {
    method: "POST",
    body: JSON.stringify({ current_password, new_password }),
  });
  writeToken(token.access_token);
}

export interface LinkCheck {
  email: string;
  full_name: string;
  purpose: LinkPurpose;
  expires_at: string;
}

/** The set-password page's first question: whose link is this, and is it
 * still good. The token goes in the body, not the URL. */
export function checkPasswordLink(token: string): Promise<LinkCheck> {
  return request<LinkCheck>("/auth/password-link/check", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function setPasswordWithLink(token: string, password: string): Promise<{ email: string }> {
  return request<{ email: string }>("/auth/password-link", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

// --- Segments (slice 3.2) ---------------------------------------------------
//
// `Definition` is left loose (`Record<string, unknown>`) on purpose: the
// shape is validated server-side by `emc_db.segments.schema`, and modelling
// the whole recursive tree here would only let the two checks drift apart.
// The builder assembles a definition of the shape it can produce and reads
// back only what it can show; anything it does not recognise on an existing
// segment is left alone rather than guessed at.

export type SegmentFieldKind = "text" | "enum" | "int" | "time" | "membership" | "attribute";

export interface SegmentField {
  field: string;
  kind: SegmentFieldKind;
  label: string;
  operators: string[];
  choices: string[];
  /** A bounded whole number's range (the migration wave's 1-7), or null. */
  bounds?: [number, number] | null;
}

export type Definition = Record<string, unknown>;

export type SegmentKind = "dynamic" | "static";
export type SegmentState =
  | "defined"
  | "requested"
  | "materialising"
  | "ready"
  | "stale"
  | "failed"
  | "archived";

export interface Segment {
  id: number;
  country: string;
  name: string;
  kind: SegmentKind;
  state: SegmentState;
  definition: Definition;
  definition_version: number;
  member_count: number | null;
  counted_at: string | null;
  materialised_at: string | null;
  /** Set while a build runs (UI-4 shows how long it has been going). */
  materialising_since?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** "" for ours; "klaviyo" for a Klaviyo engagement segment (3.2): its
   * members come from Klaviyo every day and it is read-only here. */
  source?: string;
}

/** A Klaviyo engagement segment followed here (`GET /segments/klaviyo`). */
export interface KlaviyoSegment {
  segment_id: number;
  country: string;
  name: string;
  klaviyo_id: string;
  klaviyo_name: string;
  state: SegmentState;
  /** At the last refresh: members in Klaviyo; of them found here, not
   * found, and sendable (subscribed, no live suppression). */
  klaviyo_count: number | null;
  member_count: number | null;
  not_found_count: number | null;
  sendable_count: number | null;
  refreshed_at: string | null;
  first_seen_at: string;
  /** Klaviyo no longer lists it; its last members are kept. */
  gone_at: string | null;
  /** A campaign can be assembled from it now. */
  targetable: boolean;
}

export function fetchKlaviyoSegments(country?: string): Promise<KlaviyoSegment[]> {
  const suffix = country ? `?country=${encodeURIComponent(country)}` : "";
  return request<KlaviyoSegment[]>(`/segments/klaviyo${suffix}`);
}

export interface SegmentVersion {
  version_no: number;
  definition: Definition;
  created_by: string;
  created_at: string;
}

/** One line of a segment's log: a transition, who, and why (`detail.error`). */
export interface SegmentEvent {
  action: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  detail: Record<string, unknown>;
  occurred_at: string;
}

export interface SegmentDetail extends Segment {
  versions: SegmentVersion[];
  /** Newest first, at most twenty. */
  events: SegmentEvent[];
  /** While `failed`: the reason the build or the reconciler wrote. */
  failure: string | null;
}

export interface PreviewSample {
  id: number;
  email: string;
  city: string;
  last_open_at: string | null;
  /** The value of each field the definition filters on, by field name. */
  values: Record<string, unknown>;
  /** One flag per condition, depth first — which ones this contact met. */
  matched: boolean[];
}

export interface PreviewResult {
  count: number;
  /** What a snapshot keeps: consent, suppression and the permanent hygiene
   * rules taken off. */
  sendable: number;
  /** The definition's set that is also subscribed: count − subscribed is left
   * out for consent, subscribed − mailable for a live suppression, mailable −
   * sendable by the permanent hygiene rules (broken, disposable, trap). */
  subscribed: number;
  mailable: number;
  samples: PreviewSample[];
  /** The Klaviyo handover (0028): of what the hygiene rules leave, how many
   * Klaviyo still mails (sendable is after them); N while the country is
   * migrating, null when nothing restricts it. */
  handover?: number;
  handover_wave?: number | null;
}

export function fetchSegmentFields(): Promise<SegmentField[]> {
  return request<SegmentField[]>("/segments/fields");
}

export function fetchSegments(country: string, limit?: number): Promise<Segment[]> {
  const query = new URLSearchParams();
  if (country) query.set("country", country);
  if (limit !== undefined) query.set("limit", String(limit));
  const suffix = query.toString();
  return request<Segment[]>(`/segments/${suffix ? `?${suffix}` : ""}`);
}

export function fetchSegment(id: number): Promise<SegmentDetail> {
  return request<SegmentDetail>(`/segments/${id}`);
}

export function createSegment(payload: {
  country: string;
  name: string;
  kind: SegmentKind;
  definition: Definition;
}): Promise<Segment> {
  return request<Segment>("/segments/", { method: "POST", body: JSON.stringify(payload) });
}

/** `version` is the `definition_version` the rows were loaded from: a save
 * from an older one than the stored one is a 409, not an overwrite. */
export function updateSegment(id: number, definition: Definition, version: number | null): Promise<Segment> {
  const body: Record<string, unknown> = { definition };
  if (version !== null) body.version = version;
  return request<Segment>(`/segments/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function previewSegment(
  country: string,
  definition: Definition,
  options: { sample?: number; signal?: AbortSignal } = {},
): Promise<PreviewResult> {
  const body: Record<string, unknown> = { country, definition };
  if (options.sample !== undefined) body.sample = options.sample;
  return request<PreviewResult>("/segments/preview", {
    method: "POST",
    body: JSON.stringify(body),
    signal: options.signal,
  });
}

export function materialiseSegment(id: number): Promise<Segment> {
  return request<Segment>(`/segments/${id}/materialise`, { method: "POST" });
}

export function archiveSegment(id: number): Promise<Segment> {
  return request<Segment>(`/segments/${id}/archive`, { method: "POST" });
}

/** A dynamic segment counted now; the number is kept with `counted_at` (UI-4). */
export function countSegment(id: number): Promise<Segment> {
  return request<Segment>(`/segments/${id}/count`, { method: "POST" });
}

/** A campaign that names a segment, as its audience or as an exclusion. */
export interface SegmentUse {
  segment_id: number;
  campaign_id: number;
  campaign_name: string;
  campaign_state: CampaignState;
  group_id: number | null;
  role: "audience" | "exclude";
}

export function fetchSegmentUsage(country: string): Promise<SegmentUse[]> {
  return request<SegmentUse[]>(`/segments/usage?country=${encodeURIComponent(country)}`);
}

export interface KnownValues {
  field: string;
  values: { value: string; count: number }[];
  /** Rows looked at; `complete` when that was the whole country. */
  sampled: number;
  complete: boolean;
}

/** The frequent values of a text field or an attribute, from a sample. */
export function fetchKnownValues(country: string, field: string): Promise<KnownValues> {
  const query = new URLSearchParams({ country, field });
  return request<KnownValues>(`/segments/values?${query.toString()}`);
}

export interface AttributeKeys {
  keys: { key: string; count: number; filterable: boolean }[];
  sampled: number;
  complete: boolean;
}

export function fetchAttributeKeys(country: string): Promise<AttributeKeys> {
  return request<AttributeKeys>(`/segments/attributes?country=${encodeURIComponent(country)}`);
}

// --- Campaigns (slice 1.5): the minimal admin -------------------------------

export type CampaignState =
  | "draft"
  | "scheduled"
  | "snapshotting"
  | "sending"
  | "paused"
  | "done"
  | "failed"
  | "cancelled";

export interface Campaign {
  id: number;
  name: string;
  country: string;
  state: CampaignState;
  template_version_id: number | null;
  recipient_total: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  unknown_count: number;
  delivered_count: number;
  bounced_count: number;
  complained_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignDetail extends Campaign {
  audience: Record<string, unknown>;
  chunk_size: number;
  recipient_order: "by_id" | "engaged_first";
  frequency_cap_days: number;
  ignore_send_window: boolean;
  utm: Record<string, string>;
  recipients: Record<string, number>;
  chunks: Record<string, number>;
  assembled: Record<string, unknown> | null;
  // The `campaign.hygiene` event's counts (slice 2.4); null when the campaign
  // was assembled with every hygiene rule off.
  hygiene: Record<string, unknown> | null;
  accounted_for: boolean;
}

export function fetchCampaigns(country: string): Promise<Campaign[]> {
  const query = country ? `?country=${encodeURIComponent(country)}` : "";
  return request<Campaign[]>(`/campaigns/${query}`);
}

export function fetchCampaign(id: number): Promise<CampaignDetail> {
  return request<CampaignDetail>(`/campaigns/${id}`);
}

export function createCampaign(payload: {
  name: string;
  country: string;
  /** The whole country, or `segment_id` — exactly one of the two. */
  whole_country: boolean;
  segment_id?: number | null;
  chunk_size: number;
  template_version_id: number | null;
}): Promise<Campaign> {
  return request<Campaign>("/campaigns/", { method: "POST", body: JSON.stringify(payload) });
}

export function startCampaign(id: number): Promise<Campaign> {
  return request<Campaign>(`/campaigns/${id}/start`, { method: "POST" });
}

export function pauseCampaign(id: number): Promise<Campaign> {
  return request<Campaign>(`/campaigns/${id}/pause`, { method: "POST" });
}

export function resumeCampaign(id: number): Promise<Campaign> {
  return request<Campaign>(`/campaigns/${id}/resume`, { method: "POST" });
}

export function cancelCampaign(id: number, reason: string): Promise<Campaign> {
  return request<Campaign>(`/campaigns/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// --- Overlap and exclusions (slice 3.3) -------------------------------------

export interface OverlapResult {
  a_count: number;
  b_count: number;
  both: number;
}

export function segmentOverlap(a: number, b: number): Promise<OverlapResult> {
  return request<OverlapResult>("/segments/overlap", {
    method: "POST",
    body: JSON.stringify({ a, b }),
  });
}

export type SuppressionReason =
  | "unsubscribed"
  | "hard_bounce"
  | "complaint"
  | "blocked"
  | "policy_reject"
  | "repeated_deferral"
  | "spam_trap"
  | "role_address"
  | "disposable"
  | "invalid"
  | "manual"
  | "imported"
  // Rule 1, 2026-10-16: a signal not about the address. Ends at `expires_at`.
  | "held";

/** Why a hold holds (rule 1, 2026-10-16). */
export type HoldCause = "block" | "reputation" | "policy" | "mailbox_full" | "transient";

export type SuppressionSource =
  | "import"
  | "feedback"
  | "unsubscribe"
  | "hygiene"
  | "api"
  | "manual"
  | "klaviyo";

export interface Suppression {
  id: number;
  email: string;
  country: string;
  reason: SuppressionReason;
  source: SuppressionSource;
  first_signal_at: string;
  last_signal_at: string;
  signal_count: number;
  campaign_id: number | null;
  released_at: string | null;
  released_by: string | null;
  release_note: string | null;
  /** A hold's end and cause; null on a permanent exclusion. Optional: an API
   * older than the hold leaves them out. */
  expires_at?: string | null;
  hold_cause?: HoldCause | null;
  /** False once released, or once a hold has ended. */
  is_active?: boolean;
}

export interface SuppressionPage {
  items: Suppression[];
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface SuppressionEvent {
  id: number;
  email: string;
  country: string;
  action: "suppressed" | "reinforced" | "released" | "release_denied";
  reason: SuppressionReason;
  source: SuppressionSource;
  actor: string;
  campaign_id: number | null;
  import_job_id: number | null;
  detail: Record<string, unknown>;
  occurred_at: string;
}

export interface SuppressionImportResult {
  suppressed: number;
  reinforced: number;
  invalid: number;
  sample_invalid: string[];
}

export function fetchSuppressions(params: {
  q?: string;
  reason?: string;
  source?: string;
  page?: number;
  page_size?: number;
}): Promise<SuppressionPage> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== null) query.set(key, String(value));
  }
  const suffix = query.toString();
  return request<SuppressionPage>(`/suppressions/${suffix ? `?${suffix}` : ""}`);
}

export function createSuppression(payload: {
  email: string;
  reason: string;
  country: string;
  note: string;
}): Promise<Suppression> {
  return request<Suppression>("/suppressions/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function fetchSuppressionHistory(email: string): Promise<SuppressionEvent[]> {
  return request<SuppressionEvent[]>(
    `/suppressions/${encodeURIComponent(email)}/history`,
  );
}

export function releaseSuppression(
  email: string,
  payload: { country: string; note: string },
): Promise<Suppression> {
  return request<Suppression>(`/suppressions/${encodeURIComponent(email)}/release`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Multipart, so the Content-Type header is left alone: the browser has to add
// its own boundary, and `request()` always sets application/json.
export async function importSuppressions(
  file: File,
  reason: string,
): Promise<SuppressionImportResult> {
  const body = new FormData();
  body.append("file", file);
  body.append("reason", reason);

  const token = readToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE}/suppressions/import`, {
    method: "POST",
    headers,
    body,
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: unknown };
      detail = detailText(payload.detail) ?? detail;
    } catch {
      // Not JSON — keep the status line.
    }
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as SuppressionImportResult;
}

// --- Templates and the visual editor (slice 4.2) ----------------------------
//
// `editorState` is GrapesJS's own project format and is never read here: the
// MJML source is the truth, and this is only what lets the blocks reopen as
// blocks. `null` means the version has none — hand-written or imported MJML —
// and the editor opens it from the source instead.

export type TemplateStatus = "draft" | "published" | "archived";
export type TestSendState = "requested" | "sending" | "sent" | "failed";

export interface Template {
  id: number;
  key: string;
  name: string;
  description: string;
  is_archived: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  latest_version: number | null;
  latest_status: TemplateStatus | null;
}

export interface TemplateVersion {
  id: number;
  template_id: number;
  version: number;
  status: TemplateStatus;
  source_sha256: string;
  default_language: string;
  created_by: string;
  notes: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateVariant {
  id: number;
  language: string;
  country: string;
  subject: string;
  preheader: string;
  strings: Record<string, string>;
}

export interface TemplateVersionDetail extends TemplateVersion {
  mjml_source: string;
  editor_state: Record<string, unknown> | null;
  variants: TemplateVariant[];
}

export interface TemplateDetail extends Template {
  versions: TemplateVersion[];
}

export interface TemplateVariable {
  name: string;
  kind: "contact" | "namespace" | "locked";
  label: string;
  writable: "yes" | "no";
}

export interface EmailBlock {
  id: number;
  name: string;
  mjml: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TemplatePreview {
  html: string;
  text: string;
  subject: string;
  warnings: string[];
}

export interface TemplateTestSend {
  id: number;
  version_id: number;
  state: TestSendState;
  recipients: { to?: string[]; unsubscribe_overridden?: TestSendWaiver[] };
  language: string;
  country: string;
  requested_by: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  result: { ids?: string[]; skipped?: string[]; unsubscribe_overridden?: TestSendWaiver[] };
  created_at: string;
}

/** Your own address's country unsubscribe, waived for a test letter to yourself. */
export interface TestSendWaiver {
  email: string;
  country: string;
  reason: string;
}

export function fetchTemplates(includeArchived = false): Promise<Template[]> {
  return request<Template[]>(`/templates?include_archived=${includeArchived}`);
}

export function fetchTemplate(id: number): Promise<TemplateDetail> {
  return request<TemplateDetail>(`/templates/${id}`);
}

export function createTemplate(payload: {
  key: string;
  name: string;
  description: string;
  default_language: string;
}): Promise<Template> {
  return request<Template>("/templates", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** A new template whose first draft is `id`'s newest version (UI-3). A taken
 * key is a 409 whose sentence is the marketer's. */
export function copyTemplate(
  id: number,
  payload: { key: string; name: string; default_language: string },
): Promise<Template> {
  return request<Template>(`/templates/${id}/copy`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function archiveTemplate(id: number): Promise<Template> {
  return request<Template>(`/templates/${id}/archive`, { method: "POST" });
}

export function fetchVersion(
  templateId: number,
  version: number,
): Promise<TemplateVersionDetail> {
  return request<TemplateVersionDetail>(
    `/templates/${templateId}/versions/${version}`,
  );
}

export function saveDraft(
  templateId: number,
  payload: {
    mjml_source: string;
    editor_state: Record<string, unknown> | null;
    notes: string;
    /** Which variant answers for a language that has none; the previous version's when left out. */
    default_language?: string;
    variants?: Array<{
      language: string;
      country: string;
      subject: string;
      preheader: string;
      strings: Record<string, string>;
    }>;
  },
): Promise<TemplateVersion> {
  return request<TemplateVersion>(`/templates/${templateId}/versions`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function publishVersion(
  templateId: number,
  version: number,
): Promise<TemplateVersion> {
  return request<TemplateVersion>(
    `/templates/${templateId}/versions/${version}/publish`,
    { method: "POST" },
  );
}

export function fetchTemplateVariables(): Promise<TemplateVariable[]> {
  return request<TemplateVariable[]>("/templates/variables");
}

export function fetchBlocks(): Promise<EmailBlock[]> {
  return request<EmailBlock[]>("/templates/blocks");
}

export function saveBlock(payload: {
  name: string;
  mjml: string;
}): Promise<EmailBlock> {
  return request<EmailBlock>("/templates/blocks", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteBlock(id: number): Promise<void> {
  return request<void>(`/templates/blocks/${id}`, { method: "DELETE" });
}

/** An uploaded template image (2026-10-22). `url` is absolute, on the
 * tracking host (`…/a/<sha256>.<ext>`): what goes into `<mj-image src>`. */
export interface EmailAsset {
  id: number;
  sha256: string;
  name: string;
  content_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  url: string;
  created_by: string;
  created_at: string;
}

export function fetchTemplateAssets(): Promise<EmailAsset[]> {
  return request<EmailAsset[]>("/templates/assets");
}

/** One image, multipart (so `request()`, which always says JSON, is not used).
 * The same file twice is the same asset: the answer is the stored one. */
export async function uploadTemplateAsset(file: File): Promise<EmailAsset> {
  const body = new FormData();
  body.append("file", file);
  const token = readToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${BASE}/templates/assets`, { method: "POST", headers, body });
  if (!response.ok) {
    let detail = response.status === 413 ? "A kép túl nagy (legfeljebb 5 MB)." : `HTTP ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: unknown };
      detail = detailText(payload.detail) ?? detail;
    } catch {
      // Not JSON (a proxy's own 413 page, say) — keep the sentence above.
    }
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as EmailAsset;
}

export function previewTemplate(payload: {
  mjml_source: string;
  language: string;
  country: string;
  subject: string;
  preheader: string;
  /** The variant's `t.*` texts; without them `{{ t.x }}` renders empty. */
  strings?: Record<string, string>;
  sample?: Record<string, unknown>;
}): Promise<TemplatePreview> {
  return request<TemplatePreview>("/templates/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function importHtml(payload: {
  key: string;
  name: string;
  html: string;
  default_language: string;
}): Promise<{ template: Template; warnings: string[] }> {
  return request<{ template: Template; warnings: string[] }>(
    "/templates/import-html",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function requestTestSend(
  templateId: number,
  version: number,
  payload: {
    to: string[];
    language: string;
    country: string;
    sample?: Record<string, unknown>;
  },
): Promise<TemplateTestSend> {
  return request<TemplateTestSend>(
    `/templates/${templateId}/versions/${version}/test-send`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function fetchTestSend(id: number): Promise<TemplateTestSend> {
  return request<TemplateTestSend>(`/templates/test-sends/${id}`);
}

// --- The templates gallery (UI-3) ---------------------------------------------
//
// One card per template, in one request: no source and no editor state. The
// thumbnail is a `/preview` of `latest` that each card asks for once it is on
// screen.

export interface TemplateOverviewVersion {
  id: number;
  version: number;
  status: TemplateStatus;
  default_language: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface TemplateOverviewRow {
  id: number;
  key: string;
  name: string;
  description: string;
  is_archived: boolean;
  created_by: string;
  created_at: string;
  /** The template row or any of its versions, whichever changed last. */
  edited_at: string;
  version_count: number;
  latest: TemplateOverviewVersion | null;
  /** The highest published version — what a campaign can pick today. */
  published: TemplateOverviewVersion | null;
  /** The latest version's variants; country "" serves every country. */
  languages: { language: string; country: string }[];
}

/** `klaviyoFlow`: also the copies of Klaviyo's flow templates (7.2 M5), which the gallery hides by default. */
export function fetchTemplateOverview(includeArchived = true, klaviyoFlow = false): Promise<TemplateOverviewRow[]> {
  return request<TemplateOverviewRow[]>(
    `/templates/overview?include_archived=${includeArchived}&klaviyo_flow=${klaviyoFlow}`,
  );
}

// --- Deliverability (slice 2.3) ---------------------------------------------

export interface IspDay {
  isp: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  delayed: number;
  rejected: number;
  unsubscribed: number;
}

export interface PoolDay {
  pool: string;
  day: string;
  // Recomputed from the source tables by the aggregator.
  totals: IspDay;
  // What the feedback worker counted as the events arrived. Shown next to the
  // totals rather than instead of them: where they disagree, that is the
  // interesting part.
  counters: Record<string, number>;
  by_isp: IspDay[];
}

export interface ReputationRow {
  day: string;
  source: string;
  subject: string;
  metrics: Record<string, unknown>;
  fetched_at: string;
}

export interface AlertRow {
  id: number;
  level: string;
  kind: string;
  ip_pool_id: number | null;
  pool: string | null;
  campaign_id: number | null;
  message: string;
  detail: Record<string, unknown>;
  occurred_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  notified_at: string | null;
  notify_attempts: number;
  notify_error: string | null;
}

export interface SeedCheck {
  campaign_id: number;
  score: number;
  tool: string;
  notes: string;
  actor: string;
  occurred_at: string;
}

export function fetchDaily(params: {
  from?: string;
  to?: string;
  pool?: string;
}): Promise<PoolDay[]> {
  const query = new URLSearchParams();
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  if (params.pool) query.set("pool", params.pool);
  return request<PoolDay[]>(`/deliverability/daily?${query.toString()}`);
}

export function fetchReputation(params: {
  from?: string;
  to?: string;
}): Promise<ReputationRow[]> {
  const query = new URLSearchParams();
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  return request<ReputationRow[]>(`/deliverability/reputation?${query.toString()}`);
}

// --- The watchdog (slice 2.5) -----------------------------------------------

export interface WatchEvent {
  occurred_at: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  reason: string;
  signal_id: number | null;
}

export interface PoolWatchRow {
  pool: string;
  state: string;
  since: string | null;
  reason: string;
  actor: string;
  ok_streak: number;
  is_paused: boolean;
  paused_reason: string | null;
  warmup_state: string;
  // A fraction of the warmup budget; 1 when nothing is holding the pool back.
  throttle_factor: number;
  daily_limit_per_ip: number;
  ip_count: number;
  events: WatchEvent[];
}

export interface SignalRow {
  id: number;
  source: string;
  subject_kind: string;
  subject: string;
  metric: string;
  value: string;
  level: string;
  observed_at: string;
  received_at: string;
  raw: Record<string, unknown>;
}

export function fetchWatchPools(): Promise<PoolWatchRow[]> {
  return request<PoolWatchRow[]>("/watchdog/pools");
}

export function fetchWatchSignals(params: {
  pool?: string;
  source?: string;
  days?: number;
}): Promise<SignalRow[]> {
  const query = new URLSearchParams();
  if (params.pool) query.set("pool", params.pool);
  if (params.source) query.set("source", params.source);
  if (params.days) query.set("days", String(params.days));
  return request<SignalRow[]>(`/watchdog/signals?${query.toString()}`);
}

// One cell of the 2.6 deviation panel: what one measure of one pool's provider
// did in the last closed hour, against what it usually does.
export interface AnomalyCell {
  hour: string;
  pool: string;
  isp: string;
  metric: string;
  observed: number | null;
  expected: number | null;
  // Observed over expected; null when either side cannot say.
  deviation: number | null;
  sample: number;
  profile_days: number;
}

export function fetchAnomaly(pool?: string): Promise<AnomalyCell[]> {
  const query = pool ? `?pool=${encodeURIComponent(pool)}` : "";
  return request<AnomalyCell[]>(`/watchdog/anomaly${query}`);
}

export function releasePool(pool: string, note: string): Promise<PoolWatchRow> {
  return request<PoolWatchRow>(`/watchdog/pools/${encodeURIComponent(pool)}/release`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function fetchSeedChecks(): Promise<SeedCheck[]> {
  return request<SeedCheck[]>("/deliverability/seed-checks");
}

export function fetchAlerts(openOnly = true): Promise<AlertRow[]> {
  return request<AlertRow[]>(`/alerts/?open=${openOnly ? "true" : "false"}`);
}

export function acknowledgeAlert(id: number): Promise<AlertRow> {
  return request<AlertRow>(`/alerts/${id}/acknowledge`, { method: "POST" });
}

// --- Reports and tags (slice 5.3) -------------------------------------------
//
// The report bodies are not modelled field by field on the server either: the
// shape is `emc_db.reports`', and a warehouse that did not answer sends
// `null` in every engagement column — never a zero. `number | null` here is
// that absence, and the screen shows a dash for it.

export interface TagDimension {
  id: number;
  key: string;
  label: string;
  required: boolean;
  position: number;
}

export type CampaignTags = Record<string, string[]>;

export type ReportSource = "own" | "klaviyo" | "all";

/** One audience of a Klaviyo campaign (0041): `kind` is segment, list,
 * missing (gone from the account) or null (not looked up yet). */
export interface AudienceEntry {
  id: string;
  kind: string | null;
  name: string | null;
}

export interface ReportRow {
  // null on a Klaviyo row: it is not one of ours and opens no screen.
  campaign_id: number | null;
  source: string;
  external_id: string | null;
  name: string;
  /** A Klaviyo row's subject and preview text; null on ours. */
  subject?: string | null;
  preheader?: string | null;
  /** Who a Klaviyo campaign went to and who it left out; empty on ours. */
  audience_included?: AudienceEntry[];
  audience_excluded?: AudienceEntry[];
  country: string;
  state: string;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  recipient_total: number | null;
  tags: CampaignTags;
  // Every figure can be null on a copied row: Klaviyo does not report what a
  // hygiene rule skipped, and a zero there would be a claim, not an absence.
  sent: number | null;
  unknown: number | null;
  failed: number | null;
  skipped: number | null;
  skipped_suppressed: number | null;
  skipped_hygiene: number | null;
  /** Left to Klaviyo between assembly and send (0028). */
  skipped_handover?: number | null;
  delivered: number | null;
  bounced: number | null;
  complained: number | null;
  unsubscribed: number | null;
  orders: number | null;
  revenue_eur_cents: number | null;
  /** The ECB days the euro revenue was converted at; null when none was. */
  fx_first_day?: string | null;
  fx_last_day?: string | null;
  revenue_cents: number | null;
  currency: string | null;
  attribution_window: string | null;
  pulled_at: string | null;
  delivered_permille: number | null;
  bounce_permille: number | null;
  complaint_permille: number | null;
  unsubscribe_permille: number | null;
  unique_opens: number | null;
  unique_human_opens: number | null;
  total_opens: number | null;
  /** The open headline: every contact who opened, Apple's automatic opens
   * included; of those, the ones only a machine opened for (null on a copied
   * Klaviyo row, which does not say); and the total over the delivered. */
  opens_total: number | null;
  opens_machine: number | null;
  open_rate_permille: number | null;
  unique_clicks: number | null;
  unique_human_clicks: number | null;
  ctr_permille: number | null;
  ctor_permille: number | null;
}

export interface Sources {
  postgres: boolean;
  clickhouse: boolean;
  klaviyo?: boolean;
}

export interface ReportList {
  countries: string[];
  since: string | null;
  until: string | null;
  tag: string | null;
  source: ReportSource;
  rows: ReportRow[];
  sources: Sources;
  source_unavailable: string[];
  clickhouse_error: string | null;
}

export interface TimelineBucket {
  hour: string;
  kind: string;
  total: number;
  uniq: number;
}

export interface LinkRow {
  link_no: number;
  total_clicks: number;
  unique_clicks: number;
  unique_human_clicks: number;
}

export interface IspRow {
  isp: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
}

export interface IspEngagementRow {
  isp: string;
  unique_opens: number;
  unique_human_opens: number;
  unique_clicks: number;
  unique_human_clicks: number;
}

export interface CampaignReport extends ReportRow {
  // Only one of ours is ever opened, so here the id is always there.
  campaign_id: number;
  timeline: TimelineBucket[] | null;
  links: LinkRow[] | null;
  by_isp: IspRow[];
  by_isp_clickhouse: IspEngagementRow[] | null;
  counters: Record<string, number>;
  assembly: Record<string, number> | null;
  sources: Sources;
  source_unavailable: string[];
  clickhouse_error: string | null;
}

export interface PivotRow {
  value: string;
  country: string | null;
  campaigns: number;
  sent: number;
  delivered: number;
  unique_clicks: number | null;
  ctr_permille: number | null;
  orders: number;
  revenue_eur_cents: number;
  fx_first_day?: string | null;
  fx_last_day?: string | null;
  rpr: number | null;
  rpe: number | null;
}

export interface PivotReport {
  dimension: string;
  dimension_label: string;
  countries: string[];
  since: string | null;
  until: string | null;
  campaigns: number;
  rows: PivotRow[];
  sources: Sources;
  source_unavailable: string[];
  clickhouse_error: string | null;
  note: string;
}

export interface ReportFilters {
  country?: string;
  tag?: string;
  since?: string;
  until?: string;
  source?: ReportSource;
}

function reportQuery(filters: ReportFilters): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") query.set(key, value);
  }
  const suffix = query.toString();
  return suffix ? `?${suffix}` : "";
}

export function fetchReportList(filters: ReportFilters): Promise<ReportList> {
  return request<ReportList>(`/reports/campaigns${reportQuery(filters)}`);
}

export function fetchCampaignReport(id: number): Promise<CampaignReport> {
  return request<CampaignReport>(`/reports/campaigns/${id}`);
}

export function fetchPivot(params: {
  dimension: string;
  countries?: string;
  since?: string;
  until?: string;
}): Promise<PivotReport> {
  const query = new URLSearchParams({ dimension: params.dimension });
  if (params.countries) query.set("countries", params.countries);
  if (params.since) query.set("since", params.since);
  if (params.until) query.set("until", params.until);
  return request<PivotReport>(`/reports/pivot?${query.toString()}`);
}

export function fetchTagDimensions(): Promise<TagDimension[]> {
  return request<TagDimension[]>("/tag-dimensions");
}

export function fetchCampaignTags(id: number): Promise<CampaignTags> {
  return request<CampaignTags>(`/campaigns/${id}/tags`);
}

export function saveCampaignTags(id: number, tags: CampaignTags): Promise<CampaignTags> {
  return request<CampaignTags>(`/campaigns/${id}/tags`, {
    method: "PUT",
    body: JSON.stringify(tags),
  });
}

// The CSV is not JSON, so it goes past `request()`: the token is added by
// hand and the body is kept as a blob the browser saves under its own name.
export async function downloadReportCsv(path: string, filename: string): Promise<void> {
  const token = readToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${BASE}${path}`, { headers });
  if (!response.ok) throw new ApiError(response.status, `HTTP ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// --- Campaign groups: the wizard (slice 4.3) --------------------------------

export type CampaignGroupMode = "single_language" | "localized";

export type CampaignGroupState =
  | "draft"
  | "review"
  | "scheduled"
  | "ab_testing"
  | "ab_deciding"
  | "sending"
  | "done"
  | "failed"
  | "cancelled";

export interface CampaignGroup {
  id: number;
  name: string;
  mode: CampaignGroupMode;
  state: CampaignGroupState;
  source_country: string | null;
  template_version_id: number | null;
  subject: string;
  preheader: string;
  ab: Record<string, unknown> | null;
  scheduled_local: string;
  scheduled_at: string | null;
  decide_at: string | null;
  notes: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignGroupDetail extends CampaignGroup {
  countries: Record<string, CampaignState>;
  campaign_ids: Record<string, number>;
  unapproved: string[];
  /** Localised A/B: the (country, arm) cells not approved or stale, as "RO/B". */
  unapproved_arms: string[];
  // Countries approved against a source text that has since been edited. The
  // router computes it, but `CampaignGroupDetailOut` does not declare it, so
  // the response drops it and it is absent on the wire: optional here, and
  // read from the summary (`CampaignGroupSummary.stale`) instead.
  stale?: string[];
}

export interface CountryEstimate {
  country: string;
  campaign_id: number;
  state: CampaignState;
  recipients: number;
  candidates: number;
  suppressed: number;
  frequency_capped: number;
  excluded_by_segment: number;
  not_in_audience: number;
  pool_code: string;
  pool_budget: number;
  pool_used_today: number;
  pool_room_today: number;
  send_at: string | null;
  fits_today: boolean;
  warning: string;
  /** The Klaviyo handover (0028): how many of the audience Klaviyo still
   * mails, N while the country is migrating (null: nothing restricts it), and
   * the sentence to show. Optional: an older API leaves them out. */
  handover?: number;
  handover_wave?: number | null;
  handover_message?: string;
  /** The country is parallel (0045): Klaviyo mails everybody too, and the
   * handover filters nobody — `handover_wave` is null. */
  handover_parallel?: boolean;
  /** Slice 6.2: up to when Klaviyo's suppressions for this country are known
   * here (null: never), and whether that is over an hour ago. Optional: an
   * older API leaves them out. */
  klaviyo_suppressions_synced_at?: string | null;
  klaviyo_sync_stale?: boolean;
  klaviyo_sync_message?: string;
  /** Subscribed, and gone from the country's Klaviyo account at its last full
   * walk: left out like the suppressed. Optional: an older API leaves it out. */
  absent?: number;
  /** Today under the SES account's one number (UI-11): the account's letter,
   * the country's part of its pool's day (null: the account is warmed per
   * pool), and what is left of that part today. Optional: an older API. */
  ses_account?: string;
  country_share_today?: number | null;
  country_room_today?: number;
  /** Owner, 2026-10-27: addresses another country of the group has already
   * been given the letter, so this one leaves them out. Optional: an older API. */
  group_duplicate?: number;
}

/** One country of step 1: the whole country, or the ticked segments (their
 * union) — exactly one of the two (UI-11). The exclusions and the cap go
 * with either. */
export interface CountryAudienceInput {
  country: string;
  whole_country: boolean;
  segment_ids: number[];
  exclude_segment_ids?: number[];
  chunk_size?: number;
  frequency_cap_days?: number;
}

/** A country's audience counted before it is added (`POST /audience-estimate`). */
export interface AudienceEstimate {
  country: string;
  recipients: number;
  candidates: number;
  not_in_audience: number;
  suppressed: number;
  absent: number;
  excluded_by_segment: number;
  frequency_capped: number;
  handover: number;
}

export interface GroupTestSend {
  country: string;
  /** The A/B arm this test letter carries, or null for the country's own. */
  arm: string | null;
  test_send_id: number;
  version_id: number;
  to: string[];
}

const GROUPS = "/campaign-groups";

export function fetchCampaignGroups(): Promise<CampaignGroup[]> {
  return request<CampaignGroup[]>(`${GROUPS}/`);
}

export function fetchCampaignGroup(id: number): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}`);
}

export function createCampaignGroup(payload: {
  name: string;
  mode: CampaignGroupMode;
  source_country?: string | null;
}): Promise<CampaignGroup> {
  return request<CampaignGroup>(`${GROUPS}/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function addGroupCountries(
  id: number,
  countries: CountryAudienceInput[],
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/countries`, {
    method: "POST",
    body: JSON.stringify({ countries }),
  });
}

export function setGroupLetter(
  id: number,
  templateVersionId: number,
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/letter`, {
    method: "PUT",
    body: JSON.stringify({ template_version_id: templateVersionId }),
  });
}

export function estimateAudience(input: CountryAudienceInput): Promise<AudienceEstimate> {
  return request<AudienceEstimate>(`${GROUPS}/audience-estimate`, {
    method: "POST",
    body: JSON.stringify({
      country: input.country,
      whole_country: input.whole_country,
      segment_ids: input.segment_ids,
      exclude_segment_ids: input.exclude_segment_ids ?? [],
      frequency_cap_days: input.frequency_cap_days ?? 0,
    }),
  });
}

export function fetchGroupEstimate(id: number): Promise<CountryEstimate[]> {
  return request<CountryEstimate[]>(`${GROUPS}/${id}/estimate`);
}

export function requestGroupReview(id: number): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/review`, { method: "POST" });
}

export function withdrawGroup(id: number): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/withdraw`, { method: "POST" });
}

export function scheduleGroup(
  id: number,
  atLocal: string,
  overrides: Record<string, string> = {},
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/schedule`, {
    method: "POST",
    body: JSON.stringify({ at_local: atLocal, overrides }),
  });
}

export function cancelCampaignGroup(
  id: number,
  reason: string,
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

/** `sending → failed`, an admin's decision with its reason (4.3). */
export function giveUpCampaignGroup(
  id: number,
  reason: string,
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/give-up`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function groupTestSend(
  id: number,
  emails: string[],
  countries: string[],
  arms: string[] = [],
): Promise<GroupTestSend[]> {
  return request<GroupTestSend[]>(`${GROUPS}/${id}/test-send`, {
    method: "POST",
    body: JSON.stringify({ emails, countries, arms }),
  });
}

export interface SendingIdentity {
  country: string;
  label: string;
  language: string;
  timezone: string;
  sender_name: string;
  sender_email: string;
  reply_to: string;
  company_name: string;
  company_address: string;
  unsubscribe_text: string;
  preference_center_url: string;
  send_window_start_hour: number;
  send_window_end_hour: number;
}

export function fetchSendingIdentities(): Promise<SendingIdentity[]> {
  return request<SendingIdentity[]>(`${GROUPS}/countries`);
}

// --- Sending accounts (0.1) --------------------------------------------------
// Every country's account and the verdict the campaign runner refuses a start
// on (emc_db.services.account_readiness). Read-only: the values arrive by
// migration. Whether the tracking domain resolves and answers is `emc doctor`'s.

export type FindingStatus = "ok" | "fail" | "warn";

export interface ReadinessFinding {
  /** `<field>.<what>`, e.g. `company_address.placeholder`. */
  key: string;
  status: FindingStatus;
  /** The sending_account column the finding is about, or `renders`. */
  field: string;
  /** The Hungarian sentence, printed as it is. */
  message: string;
}

export interface AccountReadiness {
  ready: boolean;
  /** What the campaign runner's gate does with a verdict that is not ready,
   * here: refuse the start (true), or record it and let a rehearsal run
   * (false — local/test/dev on the null transport). */
  enforced: boolean;
  findings: ReadinessFinding[];
}

/** EMC_ENV and EMC_TRANSPORT as the API reads them, and what the gate does. */
export interface GateDeployment {
  env: string;
  transport: string;
  enforced: boolean;
}

export interface SendingAccount {
  id: number;
  country: string;
  label: string;
  is_active: boolean;
  ses_account: string;
  ip_pool_code: string;
  quarantine_pool_code: string | null;
  sender_name: string;
  sender_email: string;
  reply_to: string;
  sending_domain: string;
  tracking_domain: string;
  company_name: string;
  company_address: string;
  tax_number: string;
  registration_number: string;
  phone: string;
  unsubscribe_text: string;
  preference_center_url: string;
  /** Slice 4.4: the company's website, printed in the footer when set. */
  website_url: string;
  language: string;
  timezone: string;
  send_window_start_hour: number;
  send_window_end_hour: number;
  frequency_cap_messages: number;
  frequency_cap_days: number;
  updated_at: string;
  readiness: AccountReadiness;
  /** Campaigns sending from it right now: while > 0, its SES account and pool
   * cannot be changed. */
  running_campaigns: number;
}

/** One of the twelve Klaviyo countries, and where a new account starts. */
export interface SettingsCountry {
  code: string;
  has_account: boolean;
  language: string;
  timezone: string;
}

export interface SettingsPool {
  code: string;
  name: string;
  ses_account: string;
}

export interface SendingAccountsView {
  /** EMC_VERIFIED_SENDING_DOMAINS, as the verdicts were reached against. */
  verified_sending_domains: string[];
  /** EMC_TRACKING_DOMAINS: the tracking hosts our edge serves. */
  tracking_domains: string[];
  deployment: GateDeployment;
  accounts: SendingAccount[];
  countries: SettingsCountry[];
  pools: SettingsPool[];
  ses_accounts: string[];
}

export function fetchSendingAccounts(): Promise<SendingAccountsView> {
  return request<SendingAccountsView>("/sending-accounts");
}

/** The fields a settings form sends (slice 4.4). */
export type SendingAccountValues = Record<string, string | number | boolean>;

export function createSendingAccount(
  country: string,
  values: SendingAccountValues,
): Promise<SendingAccount> {
  return request<SendingAccount>("/sending-accounts", {
    method: "POST",
    body: JSON.stringify({ ...values, country }),
  });
}

/** Only the fields that changed, and what the form showed for each: a field
 * another admin saved meanwhile is a 409, not an overwrite. */
export function updateSendingAccount(
  id: number,
  changes: SendingAccountValues,
  expected: SendingAccountValues,
): Promise<SendingAccount> {
  return request<SendingAccount>(`/sending-accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ changes, expected }),
  });
}

export interface SendingAccountChange {
  field: string;
  label: string;
  old: string | number | boolean | null;
  new: string | number | boolean | null;
}

export interface SendingAccountEvent {
  action: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  occurred_at: string;
  changes: SendingAccountChange[];
  ready: boolean | null;
}

export function fetchSendingAccountEvents(id: number): Promise<SendingAccountEvent[]> {
  return request<SendingAccountEvent[]>(`/sending-accounts/${id}/events`);
}

export interface FooterPreview {
  html: string;
  text: string;
}

/** The footer the values would put under every letter, rendered by the
 * sending engine. Nothing is saved. */
export function previewFooter(country: string, values: SendingAccountValues): Promise<FooterPreview> {
  return request<FooterPreview>("/sending-accounts/footer-preview", {
    method: "POST",
    body: JSON.stringify({ ...values, country }),
  });
}

export function restartGroupCountry(
  id: number,
  country: string,
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/countries/${country}/restart`, {
    method: "POST",
  });
}

export interface AbArmInput {
  key: string;
  subject: string;
  preheader?: string;
  share_permille: number;
}

export interface AbArmResult {
  key: string;
  sent: number;
  engaged: number;
  rate: number;
}

export interface AbDecision {
  campaign_id: number;
  country: string;
  winner: string;
  reason: string;
  rewritten: number;
  released: number;
  results: AbArmResult[];
}

export function setGroupAb(
  id: number,
  variants: AbArmInput[],
  decideAfterHours: number,
  metric: "open" | "click",
): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/ab`, {
    method: "PUT",
    body: JSON.stringify({
      variants,
      decide_after_hours: decideAfterHours,
      metric,
    }),
  });
}

export function clearGroupAb(id: number): Promise<CampaignGroupDetail> {
  return request<CampaignGroupDetail>(`${GROUPS}/${id}/ab`, { method: "DELETE" });
}

export function decideGroupAb(id: number, variant: string): Promise<AbDecision[]> {
  return request<AbDecision[]>(`${GROUPS}/${id}/ab/decide`, {
    method: "POST",
    body: JSON.stringify({ variant }),
  });
}

export interface Localization {
  country: string;
  subject: string;
  preheader: string;
  body_template_version_id: number | null;
  translated_by: "human" | "machine";
  approved_by: string | null;
  approved_at: string | null;
  source_hash: string;
  /** The source an approval would be of now: sent back with the approval, so
   * one that moved while it was read is refused (409). */
  source_digest: string;
}

export interface TranslateRequest {
  group_id: number;
  countries: string[];
  command: string;
}

export function fetchLocalizations(id: number): Promise<Localization[]> {
  return request<Localization[]>(`${GROUPS}/${id}/localizations`);
}

export function requestTranslation(id: number): Promise<TranslateRequest> {
  return request<TranslateRequest>(`${GROUPS}/${id}/localizations/translate`, {
    method: "POST",
  });
}

export function editLocalization(
  id: number,
  country: string,
  patch: { subject?: string; preheader?: string },
): Promise<Localization> {
  return request<Localization>(`${GROUPS}/${id}/localizations/${country}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

/** Approve against the source the screen showed (`source_digest`). */
export function approveLocalization(
  id: number,
  country: string,
  sourceDigest: string,
): Promise<Localization> {
  return request<Localization>(`${GROUPS}/${id}/localizations/${country}/approve`, {
    method: "POST",
    body: JSON.stringify({ source_digest: sourceDigest }),
  });
}

export function withdrawLocalization(
  id: number,
  country: string,
): Promise<Localization> {
  return request<Localization>(`${GROUPS}/${id}/localizations/${country}/approve`, {
    method: "DELETE",
  });
}

// --- Localised A/B: the wording matrix (4.3) --------------------------------------
//
// Rows are the countries a group still sends to, columns the A/B arms. Every
// cell is written, corrected and approved on its own; `stale` is an approval
// given against a source arm that has changed since. The schedule waits for
// every cell (the gate is the API's), and freezes each approved one onto its
// country's arm.

export type WordingStatus = "missing" | "machine" | "human" | "approved" | "stale";

export interface ArmWording {
  country: string;
  key: string;
  status: WordingStatus;
  subject: string;
  preheader: string;
  translated_by: "human" | "machine" | null;
  approved_by: string | null;
  approved_at: string | null;
  /** The source arm an approval of this cell would be of. */
  source_digest: string;
}

export interface CountryWording {
  country: string;
  language: string;
  status: WordingStatus;
  subject: string;
  preheader: string;
  translated_by: "human" | "machine" | null;
  approved_by: string | null;
  approved_at: string | null;
  arms: ArmWording[];
  /** What this country's send renders, for a preview that creates nothing:
   * its letter with its wording over it (a blank field is the letter's own),
   * or after the schedule the frozen letter as it is. */
  preview_version_id: number | null;
  preview_subject: string;
  preview_preheader: string;
  /** The source an approval of the country's own wording would be of. */
  source_digest: string;
}

export function fetchWordingMatrix(id: number): Promise<CountryWording[]> {
  return request<CountryWording[]>(`${GROUPS}/${id}/localizations/matrix`);
}

export function editArmWording(
  id: number,
  country: string,
  key: string,
  patch: { subject?: string; preheader?: string },
): Promise<ArmWording> {
  return request<ArmWording>(`${GROUPS}/${id}/localizations/${country}/arms/${key}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export function approveArmWording(
  id: number,
  country: string,
  key: string,
  sourceDigest: string,
): Promise<ArmWording> {
  return request<ArmWording>(`${GROUPS}/${id}/localizations/${country}/arms/${key}/approve`, {
    method: "POST",
    body: JSON.stringify({ source_digest: sourceDigest }),
  });
}

export function withdrawArmWording(id: number, country: string, key: string): Promise<ArmWording> {
  return request<ArmWording>(`${GROUPS}/${id}/localizations/${country}/arms/${key}/approve`, {
    method: "DELETE",
  });
}

/** "Mind jóváhagyása": the country's wording and every arm of it, or none —
 * each against the source the screen showed (the row's digest and each arm's). */
export function approveCountryWording(id: number, row: CountryWording): Promise<CountryWording> {
  return request<CountryWording>(`${GROUPS}/${id}/localizations/${row.country}/approve-all`, {
    method: "POST",
    body: JSON.stringify({
      source_digest: row.source_digest,
      arms: Object.fromEntries(row.arms.map((arm) => [arm.key, arm.source_digest])),
    }),
  });
}

// --- The campaign screens (UI-2): read-only views over the groups -----------
//
// `/overview` and `/summary` answer what the wizard's own responses do not
// carry in one request: every group's countries and numbers for the list, and
// the letter's template, the A/B subject lines and the audiences by name for
// the review page.

export interface GroupCountry {
  country: string;
  campaign_id: number;
  state: CampaignState;
  // UTC; the moment the group runner may release the country. Shown on the
  // country's own wall clock, never as UTC.
  send_at: string | null;
  recipient_total: number;
  sent_count: number;
  delivered_count: number;
  bounced_count: number;
  complained_count: number;
  failed_count: number;
  unknown_count: number;
  skipped_count: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface CampaignGroupRow extends CampaignGroup {
  countries: GroupCountry[];
}

export interface CampaignGroupOverview {
  items: CampaignGroupRow[];
  // Per state, over every group the search matches (not only this page).
  counts: Partial<Record<CampaignGroupState, number>>;
  has_more: boolean;
}

export interface NamedSegment {
  id: number;
  name: string | null;
  member_count: number | null;
}

/** A country's audience by name: the whole country, or one segment. Neither
 * is an audience the summary does not read (a list one, from before UI-10). */
export interface GroupAudience {
  whole_country: boolean;
  /** The one audience segment when there is exactly one. */
  segment: NamedSegment | null;
  exclude_segments: NamedSegment[];
  /** Every audience segment, their union (UI-11). Optional: an older API. */
  segments?: NamedSegment[];
}

export interface GroupCountryDetail extends GroupCountry {
  audience: GroupAudience;
  chunk_size: number;
  frequency_cap_days: number;
  recipient_order: "by_id" | "engaged_first";
  ignore_send_window: boolean;
  ab_winner: string | null;
  /** The country's sending account, judged when the summary was read (0.1). */
  account_readiness: AccountReadiness;
}

export interface GroupLetter {
  template_version_id: number;
  template_id: number;
  template_key: string;
  template_name: string;
  version: number;
  status: TemplateStatus;
}

export interface GroupAbArm {
  key: string;
  subject: string;
  preheader: string;
  share_permille: number;
  template_version_id: number | null;
}

export interface CampaignGroupSummary extends CampaignGroup {
  countries: GroupCountryDetail[];
  unapproved: string[];
  stale: string[];
  letter: GroupLetter | null;
  ab_arms: GroupAbArm[];
  /** Localised A/B: the (country, arm) cells not approved or stale, as "RO/B". */
  unapproved_arms: string[];
}

export interface ContactListRef {
  id: number;
  country: string;
  name: string;
  description: string;
}

export function fetchCampaignGroupOverview(params: {
  q?: string;
  states?: CampaignGroupState[];
  limit?: number;
  offset?: number;
}): Promise<CampaignGroupOverview> {
  const query = new URLSearchParams();
  if (params.q) query.set("q", params.q);
  for (const state of params.states ?? []) query.append("state", state);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  const suffix = query.toString();
  return request<CampaignGroupOverview>(`${GROUPS}/overview${suffix ? `?${suffix}` : ""}`);
}

export function fetchCampaignGroupSummary(id: number): Promise<CampaignGroupSummary> {
  return request<CampaignGroupSummary>(`${GROUPS}/${id}/summary`);
}

/** A country's lists, for the segment builder's list-membership condition.
 * Not the campaign editor's since UI-10: a campaign goes to the whole country
 * or to a segment. */
export function fetchContactLists(country: string): Promise<ContactListRef[]> {
  return request<ContactListRef[]>(`${GROUPS}/lists?country=${encodeURIComponent(country)}`);
}

/** A stored version rendered for one country, the way the sender would: the
 * subject and preheader come from the version's variant for that language
 * unless given. The endpoint takes the version id directly. */
export function previewVersion(payload: {
  version_id: number;
  language: string;
  country: string;
  subject?: string;
  preheader?: string;
}): Promise<TemplatePreview> {
  return request<TemplatePreview>("/templates/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// --- The global halt (F1), as the header bar reads it -----------------------

export interface SendingStatus {
  halted: boolean;
  id?: number | null;
  halted_at: string | null;
  halted_by: string | null;
  note: string | null;
  takes_effect_within_seconds: number | null;
}

export function fetchSendingStatus(): Promise<SendingStatus> {
  return request<SendingStatus>("/sending/status");
}

/** F1: stop every pool. The note is required and ends up on the alert. */
export function haltSending(note: string): Promise<SendingStatus> {
  return request<SendingStatus>("/sending/halt", { method: "POST", body: JSON.stringify({ note }) });
}

// --- The Klaviyo handover (0028) ------------------------------------------------------

export type HandoverStateName = "migrating" | "parallel" | "cut_over";

/** One country: migrating with N (Klaviyo excludes `emc_wave <= N`), parallel
 * (0045: Klaviyo mails everybody too, the handover filters nobody), or cut over. */
export interface Handover {
  country: string;
  state: HandoverStateName;
  wave: number;
  restricts: boolean;
  /** "A Klaviyo is küld nekik" (0045). Optional: an older API leaves it out. */
  parallel?: boolean;
  changed_at: string;
  changed_by: string;
  note: string;
  /** The automatic emc_wave write (0043) is on for the country. */
  wave_sync?: boolean;
  /** The state of the country's open wave job, if it has one. */
  wave_job?: WaveJobStateName | null;
}

export interface HandoverEvent {
  action: string;
  from_state: string | null;
  to_state: string | null;
  wave_from: number | null;
  wave_to: number | null;
  note: string;
  actor: string;
  occurred_at: string;
}

export function fetchHandover(): Promise<Handover[]> {
  return request<Handover[]>("/handover");
}

export function fetchHandoverEvents(country: string, limit = 20): Promise<HandoverEvent[]> {
  return request<HandoverEvent[]>(`/handover/${encodeURIComponent(country)}/events?limit=${limit}`);
}

export function setHandoverWave(country: string, wave: number, note: string): Promise<Handover> {
  return request<Handover>(`/handover/${encodeURIComponent(country)}/wave`, {
    method: "POST",
    body: JSON.stringify({ wave, note }),
  });
}

export function cutOverHandover(country: string, note: string): Promise<Handover> {
  return request<Handover>(`/handover/${encodeURIComponent(country)}/cut-over`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

/** "A Klaviyo is küld nekik" on or off (0045). Admin-only, with a note; 409 when
 * the country is already in that mode. */
export function setHandoverParallel(country: string, enabled: boolean, note: string): Promise<Handover> {
  return request<Handover>(`/handover/${encodeURIComponent(country)}/parallel`, {
    method: "PUT",
    body: JSON.stringify({ enabled, note }),
  });
}

export function reopenHandover(country: string, wave: number, note: string): Promise<Handover> {
  return request<Handover>(`/handover/${encodeURIComponent(country)}/reopen`, {
    method: "POST",
    body: JSON.stringify({ wave, note }),
  });
}

// --- The waves the handover hands over (0043) ------------------------------------------

export type WaveJobStateName = "requested" | "running" | "paused" | "done" | "failed" | "cancelled";

/** What the klaviyo-sync loop last said about a switched-on country. */
export type WaveLoopStatus = "writing" | "idle" | "gate_closed" | "no_key" | "rate_limited" | "failed";

/** One wave of one country: stamped, told Klaviyo, waiting to be told, not
 * tellable (no profile id, or gone from the account), mailable once N reaches it. */
export interface WaveFigure {
  wave: number;
  assigned: number;
  synced: number;
  pending: number;
  unsyncable: number;
  mailable: number;
}

/** The dry run of a wave from a followed Klaviyo segment, in the CLI's categories. */
export interface WavePreview {
  country: string;
  wave: number;
  segment_id: number;
  segment_name: string;
  klaviyo_id: string;
  refreshed_at: string | null;
  gone: boolean;
  klaviyo_members: number | null;
  matched: number;
  stamped: number;
  from_later_wave: number;
  already_earlier: number;
  not_subscribed: number;
  not_found: number;
}

export interface WaveJobResult {
  stamped: number;
  already_earlier: number;
  not_subscribed: number;
  not_found: number;
}

export interface WaveJob {
  id: number;
  country: string;
  wave: number;
  segment_id: number | null;
  segment_name: string;
  state: WaveJobStateName;
  requested_by: string;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** Members here when it was asked for, and how many it has handed on. */
  total: number | null;
  done: number;
  preview: Partial<WavePreview>;
  result: WaveJobResult;
  error: string | null;
}

export interface WaveSync {
  country: string;
  enabled: boolean;
  changed_at: string | null;
  changed_by: string | null;
  note: string;
  loop_seen_at: string | null;
  loop_status: WaveLoopStatus | null;
  loop_detail: string | null;
}

export interface WaveSyncEvent {
  from_state: string | null;
  to_state: string | null;
  actor: string;
  note: string;
  occurred_at: string;
}

/** `GET /handover/{country}/waves`: the figures, the switch, the jobs. */
export interface CountryWaves {
  country: string;
  state: HandoverStateName | null;
  n: number | null;
  restricts: boolean;
  waves: WaveFigure[];
  totals: Omit<WaveFigure, "wave">;
  mailable_now: number | null;
  next_wave: number | null;
  opens_next: number | null;
  waiting_next: number | null;
  sync: WaveSync;
  sync_events: WaveSyncEvent[];
  open_job: WaveJob | null;
  jobs: WaveJob[];
}

function wavesPath(country: string): string {
  return `/handover/${encodeURIComponent(country)}/waves`;
}

export function fetchCountryWaves(country: string): Promise<CountryWaves> {
  return request<CountryWaves>(wavesPath(country));
}

export function previewWave(country: string, wave: number, segmentId: number): Promise<WavePreview> {
  return request<WavePreview>(`${wavesPath(country)}/preview`, {
    method: "POST",
    body: JSON.stringify({ wave, segment_id: segmentId }),
  });
}

export function requestWave(country: string, wave: number, segmentId: number): Promise<WaveJob> {
  return request<WaveJob>(`${wavesPath(country)}/jobs`, {
    method: "POST",
    body: JSON.stringify({ wave, segment_id: segmentId }),
  });
}

export function cancelWaveJob(country: string, jobId: number): Promise<WaveJob> {
  return request<WaveJob>(`${wavesPath(country)}/jobs/${jobId}/cancel`, { method: "POST" });
}

export function setWaveSync(country: string, enabled: boolean, note = ""): Promise<WaveSync> {
  return request<WaveSync>(`${wavesPath(country)}/sync`, {
    method: "PUT",
    body: JSON.stringify({ enabled, note }),
  });
}

/** F1: let the sending go again, with a sentence saying why it is safe. */
export function releaseSending(note: string): Promise<SendingStatus> {
  return request<SendingStatus>("/sending/release", {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

/** The halt bar re-reads the status when a page throws or lifts the halt. */
export const HALT_CHANGED = "emc:halt-changed";

export function announceHaltChange(): void {
  window.dispatchEvent(new Event(HALT_CHANGED));
}

// --- The warmup page (UI-6) and the overview (UI-5) --------------------------
//
// `emc_api.warmup_view` says where each figure comes from. Rates travel as
// counts plus rule 4's zone, decided server-side with the rule's own exact
// comparison: the screen divides only to print, it never colours by its own
// rounding. `null` is "nothing known", never zero.

export type RateZone = "clear" | "between" | "over" | "none";

export interface WarmupRates {
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  bounce_zone: RateZone;
  complaint_zone: RateZone;
  /** Under EMC_WARMUP_MIN_SAMPLE the scheduler decides nothing on the day. */
  sample_ok: boolean;
}

export interface WarmupIsp {
  isp: string;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
}

export interface WarmupFigures {
  /** recomputed: the 2.3 morning job · live: today · none: nothing known. */
  source: "recomputed" | "live" | "none";
  rates: WarmupRates | null;
  by_isp: WarmupIsp[];
  recomputed_at: string | null;
  /** Recomputed before the day ended, or today's running count. */
  partial: boolean;
}

export interface WarmupDecision {
  day: string;
  decision: string;
  daily_limit_per_ip: number;
  budget: number;
  /** The scheduler's own words, as `warmup_day.reason` keeps them. */
  reason: string;
  actor: string;
  decided_at: string;
  /** Written by `warmup rehearse` (F8): about figures that never left. */
  rehearsal: boolean;
  basis: { sent: number; delivered: number | null; bounced: number | null; complained: number | null } | null;
}

export interface WarmupProjection {
  from_per_ip: number;
  reaches_target_on: string | null;
  days: number | null;
}

export interface WarmupPool {
  code: string;
  name: string;
  ses_account: string;
  countries: string[];
  /** The zone the pool's times are shown in; `timezones` lists them all. */
  timezone: string;
  timezones: string[];
  ip_count: number;
  daily_limit_per_ip: number;
  target_daily_per_ip: number;
  warmup_step: number;
  throttle_factor: number;
  warmup_state: string;
  is_paused: boolean;
  paused_reason: string | null;
  watch: { state: string; since: string | null; reason: string; actor: string };
  /** What the limiter allows today, with the watchdog's throttle… */
  budget_today: number;
  /** …and what the warmup alone would allow. */
  full_budget_today: number;
  today: WarmupFigures;
  yesterday: WarmupFigures & { day: string };
  decision: WarmupDecision | null;
  decided_today: boolean;
  spark: { day: string; sent: number | null }[];
  projection: WarmupProjection | null;
  /** A release now is undone by the scheduler's next tick (KERDESEK 3.11). */
  repause_risk: boolean;
  /** Whose figures it would stop the pool on: today's, or — with no decision
   * for today yet — yesterday's, which the tick after a release decides on. */
  repause_basis: "today" | "yesterday" | null;
  can_start: boolean;
  can_pause: boolean;
  can_release: boolean;
  /** watch: the watchdog's release (both machines) · warmup: the warmup's resume. */
  release_path: "watch" | "warmup" | null;
  /** The SES account whose daily number this pool's limit is a share of
   * (2026-10-23), or null for a pool warmed on its own. */
  warmup_account?: string | null;
}

/** One pool's share of an SES account's number, and the counts behind it. */
export interface WarmupAccountShare {
  code: string;
  mailable: number;
  share: number;
  countries: WarmupAccountCountry[];
}

/** One country of a pool's share (2026-10-25): its own part of the pool's
 * day, what the limiter allows it today, and what it has sent. `share` and
 * `budget_today` are null on a split written before the parts. */
export interface WarmupAccountCountry {
  country: string;
  mailable: number;
  share?: number | null;
  budget_today?: number | null;
  sent_today?: number;
  /** What today's campaigns still have to send there (2026-10-26). */
  recipients?: number | null;
}

export interface WarmupAccountPool {
  code: string;
  name: string;
  warmup_state: string;
  is_paused: boolean;
  paused_reason: string | null;
  held_by_watch: boolean;
  budget_today: number;
  sent_today: number;
}

export interface WarmupAccountEvent {
  /** start | decision | step_back | limit */
  action: string;
  actor: string;
  occurred_at: string;
  old: number | null;
  new: number | null;
  note: string;
}

/** One SES account warmed as one: the owner's daily number and its split. */
export interface WarmupAccount {
  code: string;
  daily_limit: number;
  target_daily: number;
  /** Whether the ramp may raise the number after a clean day. */
  ramp: boolean;
  warmup_step: number;
  /** cold | warming | warm | paused */
  state: string;
  /** AWS's 24-hour quota for the account (EMC_SES_DAILY_QUOTAS). */
  ses_daily_quota: number;
  over_quota: boolean;
  sent_today: number;
  budget_today: number;
  split_at: string | null;
  mailable: number;
  /** campaigns | mailable: what the split rests on (2026-10-26). */
  basis?: string;
  /** Recipients today's campaigns still have to send, account-wide. */
  recipients?: number;
  split: WarmupAccountShare[];
  pools: WarmupAccountPool[];
  decision: {
    day: string;
    today: boolean;
    decision: string;
    daily_limit: number;
    reason: string;
    actor: string;
    decided_at: string;
  } | null;
  events: WarmupAccountEvent[];
  can_start: boolean;
  can_set_limit: boolean;
  can_pause: boolean;
  can_release: boolean;
}

export interface WarmupKnobs {
  start_per_ip: number;
  growth: number;
  min_sample: number;
}

/** Rule 4, in permille. */
export interface WarmupThresholds {
  raise_bounce_below: number;
  raise_complaint_below: number;
  pause_bounce_above: number;
  pause_complaint_above: number;
}

export interface WarmupBoard {
  now: string;
  today: string;
  knobs: WarmupKnobs;
  thresholds: WarmupThresholds;
  accounts?: WarmupAccount[];
  pools: WarmupPool[];
}

export interface WarmupDay {
  day: string;
  today: boolean;
  /** The warmup's limit × IPs: what the day was given, not what a throttle left of it. */
  budget: number | null;
  daily_limit_per_ip: number | null;
  figures: WarmupFigures;
  decision: WarmupDecision | null;
  /** Whether a machine held the pool back at some moment of the day. */
  held: "paused" | "throttled" | null;
  plan: number | null;
}

export interface WarmupCurvePoint {
  day: string;
  actual: number | null;
  plan: number | null;
  budget: number | null;
}

export interface WarmupCurve {
  points: WarmupCurvePoint[];
  now_index: number | null;
}

export interface WarmupPlan {
  start_day: string;
  start_per_ip: number;
  target_per_ip: number;
  reaches_target_on: string | null;
  /** A cold pool's plan: the ramp it would climb if it were started today. */
  hypothetical: boolean;
  growth: number;
}

export interface WarmupDays {
  pool: string;
  today: string;
  days: WarmupDay[];
  curve: WarmupCurve;
  plan: WarmupPlan | null;
  projection: WarmupProjection | null;
  knobs: WarmupKnobs;
  thresholds: WarmupThresholds;
}

export interface WarmupEvent {
  source: "warmup" | "watch" | "alert";
  id: number;
  occurred_at: string;
  action: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  text: string;
  decision: string | null;
  per_ip: number | null;
  level: string | null;
  kind: string | null;
  acknowledged: boolean | null;
  rehearsal: boolean;
}

export interface Overview {
  now: string;
  today: string;
  totals: { sent_today: number; budget_today: number };
  pools: WarmupPool[];
  curve: WarmupCurve;
  /** The last 24 hours, unacknowledged first. */
  alerts: AlertRow[];
  knobs: WarmupKnobs;
  thresholds: WarmupThresholds;
}

const WARMUP = "/warmup/pools";

function poolPath(code: string): string {
  return `${WARMUP}/${encodeURIComponent(code)}`;
}

export function fetchWarmupBoard(): Promise<WarmupBoard> {
  return request<WarmupBoard>(WARMUP);
}

export function fetchWarmupPool(code: string): Promise<WarmupPool> {
  return request<WarmupPool>(poolPath(code));
}

export function fetchWarmupDays(code: string, days: number): Promise<WarmupDays> {
  return request<WarmupDays>(`${poolPath(code)}/days?days=${days}`);
}

export function fetchWarmupEvents(code: string, limit: number): Promise<WarmupEvent[]> {
  return request<WarmupEvent[]>(`${poolPath(code)}/events?limit=${limit}`);
}

/** `cold -> warming`, the CLI's `warmup start`. `perIp` null: the configured start. */
export function startWarmup(code: string, note: string, perIp: number | null): Promise<WarmupPool> {
  return request<WarmupPool>(`${poolPath(code)}/start`, {
    method: "POST",
    body: JSON.stringify(perIp === null ? { note } : { note, per_ip: perIp }),
  });
}

export function pauseWarmupPool(code: string, note: string): Promise<WarmupPool> {
  return request<WarmupPool>(`${poolPath(code)}/pause`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function releaseWarmupPool(code: string, note: string): Promise<WarmupPool> {
  return request<WarmupPool>(`${poolPath(code)}/release`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

function accountPath(code: string): string {
  return `/warmup/accounts/${encodeURIComponent(code)}`;
}

/** Every cold pool of the account -> warming, at the person's number. */
export function startWarmupAccount(
  code: string,
  dailyLimit: number,
  note: string,
  ramp: boolean,
): Promise<WarmupAccount> {
  return request<WarmupAccount>(`${accountPath(code)}/start`, {
    method: "POST",
    body: JSON.stringify({ daily_limit: dailyLimit, note, ramp }),
  });
}

/** The account's number, any, at once — with the reason on it. */
export function setWarmupAccountLimit(
  code: string,
  dailyLimit: number,
  note: string,
  ramp: boolean,
): Promise<WarmupAccount> {
  return request<WarmupAccount>(`${accountPath(code)}/limit`, {
    method: "POST",
    body: JSON.stringify({ daily_limit: dailyLimit, note, ramp }),
  });
}

export function pauseWarmupAccount(code: string, note: string): Promise<WarmupAccount> {
  return request<WarmupAccount>(`${accountPath(code)}/pause`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function releaseWarmupAccount(code: string, note: string): Promise<WarmupAccount> {
  return request<WarmupAccount>(`${accountPath(code)}/release`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function fetchOverview(): Promise<Overview> {
  return request<Overview>("/overview");
}

// --- Why an address did or did not get a campaign (3.3, rule 6) -------------

export interface Explanation {
  campaign_id: number;
  email: string;
  contact_id: number | null;
  verdict: string;
  detail: Record<string, unknown>;
  evaluated_at: string;
}

export function explainAddress(campaignId: number, email: string): Promise<Explanation> {
  return request<Explanation>(
    `/campaigns/${campaignId}/explain?email=${encodeURIComponent(email)}`,
  );
}

// --- Webshop events (slice 7.1) ---------------------------------------------
//
// `payload` stays `Record<string, unknown>`: it is the shop's own shape, the
// server never reads past `name` on a custom event, and modelling it here
// would only be a second place for the two to disagree.

export const WEBSHOP_EVENT_TYPES = [
  "registered",
  "viewed_product",
  "added_to_cart",
  "started_checkout",
  "abandoned_cart",
  "placed_order",
  "custom",
] as const;

export type WebshopEventType = (typeof WEBSHOP_EVENT_TYPES)[number];

export interface WebshopEvent {
  id: number;
  country: string;
  event_type: WebshopEventType;
  external_id: string;
  email_raw: string;
  email_normalized: string;
  contact_id: number | null;
  match_state: "unmatched" | "matched";
  occurred_at: string;
  received_at: string;
  payload: Record<string, unknown>;
  source: string;
  ingest_id: string;
  stream_state: "pending" | "streamed";
  streamed_at: string | null;
}

export interface WebshopEventPage {
  items: WebshopEvent[];
  next_cursor: number | null;
}

export interface WebshopEventStats {
  country: string;
  hours: number;
  total: number;
  by_type: Record<string, number>;
  unmatched: number;
  stream_pending: number;
}

export function fetchWebshopEvents(params: {
  country?: string;
  type?: string;
  email?: string;
  since?: string;
  until?: string;
  cursor?: number | null;
  limit?: number;
}): Promise<WebshopEventPage> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== null) query.set(key, String(value));
  }
  const suffix = query.toString();
  return request<WebshopEventPage>(`/events${suffix ? `?${suffix}` : ""}`);
}

export function fetchWebshopEventStats(params: {
  country?: string;
  hours?: number;
}): Promise<WebshopEventStats> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const suffix = query.toString();
  return request<WebshopEventStats>(`/events/stats${suffix ? `?${suffix}` : ""}`);
}

// --- Contacts (slice 0.3) ---------------------------------------------------

export type ConsentStatus =
  | "subscribed"
  | "unsubscribed"
  | "never_subscribed"
  | "pending";

export interface Contact {
  id: number;
  email: string;
  country: string;
  consent_status: ConsentStatus;
  first_name: string;
  last_name: string;
  city: string;
  language: string;
  source: string;
  created_at: string;
  last_open_at: string | null;
  last_click_at: string | null;
  is_suppressed: boolean;
  /** Whether the profile is still in this country's Klaviyo account; an
   * absent contact is not sendable (UI-10). Optional: an API older than the
   * field leaves it out, and a missing value reads as present. */
  present?: boolean;
}

export interface ContactPage {
  items: Contact[];
  next_cursor: number | null;
}

export interface ContactSuppression {
  id: number;
  country: string;
  reason: SuppressionReason;
  source: SuppressionSource;
  actor: string;
  first_signal_at: string;
  last_signal_at: string;
  signal_count: number;
  campaign_id: number | null;
  detail: Record<string, unknown>;
  created_at: string;
  released_at: string | null;
  released_by: string | null;
  release_note: string | null;
  /** A hold's end; its cause is `detail.cause`. Null when permanent. */
  expires_at?: string | null;
  is_active: boolean;
}

export interface ContactDeliveryEvent {
  id: number;
  campaign_id: number | null;
  event_type: string;
  bounce_type: string | null;
  bounce_subtype: string | null;
  delay_type: string | null;
  complaint_feedback_type: string | null;
  smtp_status: string | null;
  diagnostic: string | null;
  occurred_at: string;
}

export interface ContactListMembership {
  list_id: number;
  name: string;
  country: string;
  joined_at: string;
  source: string;
}

export interface ContactDetail extends Contact {
  consent_source: string;
  consent_at: string | null;
  external_id: string | null;
  timezone: string;
  migration_wave: number | null;
  wave_synced_at: string | null;
  opens_90d: number;
  clicks_90d: number;
  emails_received_30d: number;
  last_order_at: string | null;
  orders_count: number;
  ltv_cents: number;
  updated_at: string;
  attributes: Record<string, unknown>;
  sendable: boolean;
  sendable_reason: string;
  suppressions: ContactSuppression[];
  suppression_events: SuppressionEvent[];
  delivery_events: ContactDeliveryEvent[];
  lists: ContactListMembership[];
  /** The address in every country we hold it (UI-9). Absent on an API
   * older than the field: the detail then shows nothing for it. */
  countries?: ContactCountry[];
}

/** The exclusion that stops the address in one country: its global row, or
 * its row in that country. */
export interface ContactCountrySuppression {
  reason: SuppressionReason;
  scope: "country" | "global";
  source: SuppressionSource;
  since: string;
  /** A hold's end and cause (rule 1, 2026-10-16); null when permanent. */
  until?: string | null;
  cause?: HoldCause | null;
}

/** One country's row for this address (UI-9). */
export interface ContactCountry {
  country: string;
  contact_id: number;
  consent_status: ConsentStatus;
  sendable: boolean;
  suppression: ContactCountrySuppression | null;
  /** Whether the profile is still in that country's Klaviyo account (UI-10).
   * Optional: missing reads as present. */
  present?: boolean;
}

/** One country's contacts, counted (UI-9); `country` is "" for all of them. */
export interface ContactCounts {
  country: string;
  total: number;
  subscribed: number;
  unsubscribed: number;
  never_subscribed: number;
  pending: number;
  suppressed: number;
  sendable: number;
  /** How many of them are no longer in the country's Klaviyo account
   * (UI-10). Optional: an API older than the field leaves it out, and the
   * figure is not shown then. */
  absent?: number;
}

/** The tabs over the contacts list and their figures: one grouped count,
 * kept on the server for `fresh_seconds` and read at `computed_at`. */
export interface ContactSummary {
  computed_at: string;
  fresh_seconds: number;
  overall: ContactCounts;
  countries: ContactCounts[];
}

export function fetchContactSummary(): Promise<ContactSummary> {
  return request<ContactSummary>("/contacts/summary");
}

export function fetchContacts(params: {
  search?: string;
  country?: string;
  consent?: string;
  suppressed?: boolean | null;
  cursor?: number | null;
  limit?: number;
}): Promise<ContactPage> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== null) query.set(key, String(value));
  }
  const suffix = query.toString();
  return request<ContactPage>(`/contacts${suffix ? `?${suffix}` : ""}`);
}

export function fetchContact(id: number): Promise<ContactDetail> {
  return request<ContactDetail>(`/contacts/${id}`);
}

/** The contact's `contact_engagement` row (5.5): per message, our own sends
 * only, rebuilt nightly at `computed_at`. */
export interface ContactEngagementFigures {
  first_sent_at: string;
  last_sent_at: string;
  last_delivered_at: string | null;
  received_total: number;
  received_30d: number;
  received_60d: number;
  received_90d: number;
  received_180d: number;
  received_365d: number;
  opened_30d: number;
  opened_60d: number;
  opened_90d: number;
  opened_180d: number;
  opened_365d: number;
  clicked_30d: number;
  clicked_60d: number;
  clicked_90d: number;
  clicked_180d: number;
  clicked_365d: number;
  last5_opened: number;
  last5_clicked: number;
  last10_opened: number;
  last10_clicked: number;
  last_human_click_at: string | null;
  computed_at: string;
}

/** One campaign the contact was in, newest first: sent (or may have been),
 * or — rule 6 — not sent, with `state` and the row's `reason` saying why. */
export interface ContactMessage {
  campaign_id: number;
  campaign_name: string;
  /** The campaign's group: the campaign page's id. */
  group_id: number | null;
  /** What the list is ordered by: the send, or the campaign's start. */
  at: string;
  sent_at: string | null;
  state: string;
  delivery_state: string;
  sent: boolean;
  received: boolean;
  reason: string | null;
  /** `person`: a person opened it or clicked in it; `machine`: only a
   * machine fetched it (Apple Mail, a scanner, or not classified yet). */
  opened: "person" | "machine" | "none";
  opened_at: string | null;
  clicked: boolean;
  clicked_at: string | null;
  pool: string | null;
}

export interface ContactMessages {
  figures: ContactEngagementFigures | null;
  last_human_open_at: string | null;
  items: ContactMessage[];
  next_cursor: string | null;
}

export function fetchContactMessages(id: number, cursor: string | null = null): Promise<ContactMessages> {
  const suffix = cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  return request<ContactMessages>(`/contacts/${id}/messages${suffix}`);
}

// --- Klaviyo signals for the flows in shadow (7.2 M2) ------------------------

/** One Klaviyo event of a contact, as the pull stored it. `flow_source_id`
 * and `message_source_id` are Klaviyo's `$flow` and `$message`. */
export interface ContactKlaviyoEvent {
  id: number;
  metric: string;
  occurred_at: string;
  external_id: string;
  profile_source_id: string | null;
  flow_source_id: string | null;
  message_source_id: string | null;
  /** `$value` in whole cents of `currency`. */
  value_cents: number | null;
  currency: string | null;
  properties: Record<string, unknown>;
}

export interface ContactKlaviyoEvents {
  items: ContactKlaviyoEvent[];
  next_cursor: string | null;
}

export function fetchContactKlaviyoEvents(
  id: number,
  cursor: string | null = null,
): Promise<ContactKlaviyoEvents> {
  const suffix = cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  return request<ContactKlaviyoEvents>(`/contacts/${id}/klaviyo-events${suffix}`);
}

/** One pulled stream: a country's metric, list or segment, and how far it is
 * stored (`complete_until`, the watermark). */
export interface KlaviyoSignalSource {
  id: number;
  country: string;
  kind: "metric" | "list" | "segment";
  source_id: string;
  name: string;
  state: "active" | "paused" | "failed" | "retired";
  backfill_from: string;
  complete_until: string | null;
  /** Seconds the watermark is behind now; null before the first walk ended. */
  lag_seconds: number | null;
  /** A walk is part-way (a first backfill, a rate limit). */
  walking: boolean;
  last_tick_at: string | null;
  stored: number;
  stragglers: number;
  calls: number;
  last_error: string | null;
  last_error_at: string | null;
}

export function fetchKlaviyoSignalSources(): Promise<KlaviyoSignalSource[]> {
  return request<KlaviyoSignalSource[]>("/klaviyo/signal-sources");
}

// --- Flows (7.2 M1) -------------------------------------------------------------
//
// What the hourly inventory copied out of Klaviyo, read-only. A definition is
// format 1 (`emc_db/flows/definition.py`): anything the normaliser did not
// understand is `{kind: "unsupported", raw}` in its place.

/** Something Klaviyo sent that format 1 does not know, kept as it came. A
 * step keeps the links that could still be read. */
export interface FlowUnsupported {
  kind: "unsupported";
  raw: unknown;
  next?: string | null;
  yes?: string | null;
  no?: string | null;
}

export interface FlowWindow {
  kind: "all_time" | "since_flow_start" | "last";
  quantity?: number;
  unit?: string;
}

export interface FlowWhere {
  property: string;
  type: string;
  op: string;
  value: unknown;
}

export type FlowLeaf =
  | {
      kind: "metric_count";
      metric: string | null;
      source_id: string;
      op: string;
      value: number;
      window: FlowWindow;
      where: FlowWhere[];
    }
  | { kind: "not_in_flow"; window: FlowWindow }
  | { kind: "event_property"; source_id: string; property: string; type: string; op: string; value: unknown }
  /** A random share of the profiles (Klaviyo's "random sample"): a split's only. */
  | { kind: "sample"; percent: number }
  | FlowUnsupported;

export type FlowGroup = { any: FlowLeaf[] } | FlowUnsupported;

export type FlowCondition = { all: FlowGroup[] } | FlowUnsupported;

export interface FlowMessage {
  /** Klaviyo's message id; null for a letter written in the editor (M7). */
  source_id: string | null;
  name: string | null;
  subject: string | null;
  preheader: string | null;
  from_email: string | null;
  from_label: string | null;
  reply_to: string | null;
  /** Klaviyo's template id; `template_id` is ours, after the import (M5). */
  template_source_id: string | null;
  template_id: number | null;
  smart_sending: boolean;
  transactional: boolean;
  add_tracking_params: boolean;
  tracking_params: { param: string; value: string }[] | null;
  filter: FlowCondition | null;
}

export interface FlowVariant {
  id: string;
  weight: number;
  status: string;
  message: FlowMessage | FlowUnsupported;
}

export type FlowStep =
  | {
      kind: "delay";
      unit: string;
      value: number;
      secondary: number;
      until_time: string | null;
      weekdays: number[] | null;
      timezone: string;
      next: string | null;
    }
  | { kind: "email"; status: string; message: FlowMessage | FlowUnsupported; next: string | null }
  | {
      kind: "ab";
      status: string;
      experiment: string;
      main_id: string;
      main_status: string;
      main: FlowMessage | FlowUnsupported;
      variants: FlowVariant[];
      meta: Record<string, unknown>;
      next: string | null;
    }
  | { kind: "split"; condition: FlowCondition; yes: string | null; no: string | null }
  | {
      /** Klaviyo's multi-branch split: the branches tried in order, the first
       * whose condition holds taken, else `otherwise`. Edited in Klaviyo. */
      kind: "multi_split";
      name: string | null;
      branches: FlowBranch[];
      otherwise: FlowBranchElse;
    }
  | { kind: "noop"; klaviyo_type: string; status: string; data: Record<string, unknown>; next: string | null }
  | FlowUnsupported;

export interface FlowBranch {
  id: string;
  name: string | null;
  condition: FlowCondition;
  next: string | null;
}

export interface FlowBranchElse {
  id: string;
  name: string | null;
  next: string | null;
}

export type FlowTriggerDefinition =
  | {
      kind: "metric" | "list" | "segment";
      metric: string | null;
      /** Null in a new flow until a metric, list or segment is chosen. */
      source_id: string | null;
      filter: FlowCondition | null;
    }
  | FlowUnsupported;

export interface FlowProblem {
  path: string;
  reason: string;
}

export interface FlowDefinition {
  format: number;
  trigger: FlowTriggerDefinition;
  filter: FlowCondition | null;
  /** Enter again only after this long (Klaviyo's re-entry criteria); once
   * ever, or every time the trigger comes. */
  reentry:
    | { quantity: number; unit: string }
    | { kind: "once" }
    | { kind: "every_time" }
    | FlowUnsupported
    | null;
  entry: string | null | FlowUnsupported;
  steps: Record<string, FlowStep>;
  unsupported: FlowProblem[];
  extra?: Record<string, FlowUnsupported>;
}

/** The trigger in short, as the list shows it. */
export interface FlowTrigger {
  kind: string;
  metric: string | null;
  source_id: string | null;
  /** A list's or segment's name, when a copy here knows it. */
  source_name: string | null;
  /** The trigger filters the event's properties. */
  filtered: boolean;
}

export type FlowMode = "off" | "shadow";

export interface FlowRow {
  id: number;
  country: string;
  name: string;
  origin: "klaviyo" | "emc";
  flow_external_id: string | null;
  mode: FlowMode;
  follows_klaviyo: boolean;
  /** live, manual, draft, archived — or gone: Klaviyo no longer lists it. */
  source_status: string | null;
  klaviyo_changed_at: string | null;
  /** The definition seen last in Klaviyo is not the one in force. */
  change_not_followed: boolean;
  shadow_since: string | null;
  trigger: FlowTrigger | null;
  current_version: number | null;
  versions: number;
  steps: number;
  emails: number;
  supported: boolean | null;
  problems: number;
  created_at: string;
  updated_at: string;
  /** The open draft's number, if somebody is editing the flow (M7). */
  draft_version: number | null;
}

export interface FlowVersionRow {
  number: number;
  state: "draft" | "published" | "retired";
  supported: boolean;
  problems: number;
  definition_hash: string;
  source_definition_id: number | null;
  source_first_seen_at: string | null;
  created_by: string;
  note: string | null;
  created_at: string;
  /** A draft's save counter: what a save or a publish sends back (M7). */
  revision: number;
  /** Published from the editor: running runs move to it at their next step. */
  migrate_runs: boolean;
  /** The version a draft was started from. */
  based_on: number | null;
  /** The reason it was published with, from its log. */
  reason: string | null;
}

export interface FlowVersionEvent {
  action: string;
  from_state: string | null;
  to_state: string | null;
  actor: string;
  detail: Record<string, unknown>;
  occurred_at: string;
}

export interface FlowVersionDetail extends FlowVersionRow {
  definition: FlowDefinition;
  unsupported: FlowProblem[];
  events: FlowVersionEvent[];
  /** The inventory's version of the same Klaviyo definition: what the
   * editor's "Klaviyóból" / "módosítva" marks compare against. */
  klaviyo_base: number | null;
}

export interface FlowDetail extends FlowRow {
  version: FlowVersionDetail | null;
}

export function fetchFlows(
  params: { country?: string; mode?: string; changed_since?: string } = {},
): Promise<FlowRow[]> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, value);
  }
  const suffix = query.toString();
  return request<FlowRow[]>(`/flows/${suffix ? `?${suffix}` : ""}`);
}

export function fetchFlow(id: number): Promise<FlowDetail> {
  return request<FlowDetail>(`/flows/${id}`);
}

export function fetchFlowVersions(id: number): Promise<FlowVersionRow[]> {
  return request<FlowVersionRow[]>(`/flows/${id}/versions`);
}

export function fetchFlowVersion(id: number, number: number): Promise<FlowVersionDetail> {
  return request<FlowVersionDetail>(`/flows/${id}/versions/${number}`);
}

// --- The flow engine in shadow (7.2 M3) ---
//
// Shadow decides and never sends: a decision is a row, there is no live mode.
// The flow page switches a flow in and out of shadow and reads its numbers
// per step; the contact page reads the contact's runs in full (rule 6).

/** FlowRunState: where a run of a flow is. */
export type FlowRunState =
  | "pending"
  | "waiting"
  | "running"
  | "completed"
  | "exited"
  | "rejected"
  | "failed"
  | "unknown";

/** FlowDecisionOutcome: what the letter step would have done. */
export type FlowDecisionOutcome = "send" | "skip" | "held_manual";

export interface FlowModeChange {
  id: number;
  mode: FlowMode;
  shadow_since: string | null;
}

/** Shadow on or off, with a reason (3–500 characters). Admin only (403);
 * 409 when the flow is already in that mode. */
export function setFlowMode(id: number, mode: FlowMode, reason: string): Promise<FlowModeChange> {
  return request<FlowModeChange>(`/flows/${id}/mode`, {
    method: "POST",
    body: JSON.stringify({ mode, reason }),
  });
}

/** A flow a "switch them all" (7.2 M5) would take — or leaves out, and why:
 * `already` (in that mode), `unsupported`, `no_version`, `not_live_in_klaviyo`. */
export interface FlowModeCandidate {
  id: number;
  country: string;
  name: string;
  mode: FlowMode;
  source_status: string | null;
  supported: boolean | null;
  left_out: string | null;
}

export interface FlowModeCandidates {
  target: FlowMode;
  country: string | null;
  flows: FlowModeCandidate[];
}

export interface FlowModesChange {
  switched: { flow: number; country: string; name: string; mode: FlowMode }[];
  refused: { flow: number; error: string }[];
}

/** What switching every flow of the country ("": of every country) would do. */
export function fetchFlowModeCandidates(target: FlowMode, country: string): Promise<FlowModeCandidates> {
  const query = new URLSearchParams({ target });
  if (country) query.set("country", country);
  return request<FlowModeCandidates>(`/flows/mode/candidates?${query.toString()}`);
}

/** The flows the admin confirmed, each switched and logged on its own. Admin only. */
export function setFlowModes(flowIds: number[], mode: FlowMode, reason: string): Promise<FlowModesChange> {
  return request<FlowModesChange>("/flows/mode", {
    method: "POST",
    body: JSON.stringify({ mode, reason, flow_ids: flowIds }),
  });
}

export interface FlowShadowStepStats {
  step_id: string;
  /** Step rows: how many times a run went through this step. */
  reached: number;
  /** Letter decisions: would send, would skip, held for manual approval. */
  sent: number;
  skipped: number;
  held_manual: number;
  /** By the step's result: `wait`, `yes`, `no`, `passed:draft`, a decision reason… */
  results: Record<string, number>;
  /** Letter decisions by outcome, then by reason. */
  decisions: Record<string, Record<string, number>>;
}

/** Why the engine cannot decide this flow now; `text` is already Hungarian. */
export interface FlowEngineBlock {
  code: string;
  text: string;
  stream?: string;
}

export interface FlowEngineStatus {
  blocked: FlowEngineBlock[];
  /** Decided up to here: the lowest point every stream it reads is complete to. */
  watermark: string | null;
  checked_at: string | null;
  /** Trigger signals taken in as runs. */
  taken_in: number;
  /** Trigger signals passed over (no Klaviyo profile on them). */
  passed_over: number;
  /** The intake bound: signals before it cannot decide anything after the shadow began. */
  intake_from?: string | null;
  /** Trigger signals passed over because they were before the intake bound. */
  out_of_bound?: number;
}

export interface FlowShadowStats {
  flow_id: number;
  since: string | null;
  until: string | null;
  /** True: the history rebuilt from before shadow is counted too. */
  replay: boolean;
  /** By step id. `reached`, `sent` and `skipped` are what the editor's canvas
   * reads (F7's `FlowStepStats`); the rest is the flow page's. */
  steps: Record<string, FlowShadowStepStats>;
  /** The number of the version in force. */
  version: number | null;
  runs_by_state: Record<string, number>;
  engine: FlowEngineStatus;
}

export function fetchFlowShadowStats(
  id: number,
  params: { since?: string; until?: string; replay?: boolean } = {},
): Promise<FlowShadowStats> {
  const query = new URLSearchParams();
  if (params.since) query.set("since", params.since);
  if (params.until) query.set("until", params.until);
  if (params.replay !== undefined) query.set("replay", String(params.replay));
  const suffix = query.toString();
  return request<FlowShadowStats>(`/flows/${id}/stats${suffix ? `?${suffix}` : ""}`);
}

export interface FlowRunRow {
  id: number;
  flow_id: number;
  flow_name: string | null;
  country: string;
  profile_source_id: string | null;
  email: string | null;
  contact_id: number | null;
  trigger_kind: string;
  trigger_key: string;
  trigger_at: string | null;
  state: FlowRunState;
  /** Where it waits — or, for an exited run, the step it did not reach. */
  step_id: string | null;
  wake_at: string | null;
  entered: boolean;
  /** Some fact it read was incomplete. */
  partial: boolean;
  outcome_reason: string | null;
  version_id: number | null;
  current_version_id: number | null;
  created_at: string | null;
  ended_at: string | null;
}

export interface FlowRunPage {
  runs: FlowRunRow[];
  /** The `before` of the next page; null on the last. */
  next: number | null;
}

export function fetchFlowRuns(
  id: number,
  params: { state?: string; before?: number; limit?: number } = {},
): Promise<FlowRunPage> {
  const query = new URLSearchParams();
  if (params.state) query.set("state", params.state);
  if (params.before !== undefined) query.set("before", String(params.before));
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return request<FlowRunPage>(`/flows/${id}/runs${suffix ? `?${suffix}` : ""}`);
}

/** One move of a run (`flow_run_event`): what the step read and answered. */
export interface FlowRunEventRow {
  action: string;
  from: string | null;
  to: string | null;
  actor: string;
  at: string | null;
  detail: Record<string, unknown>;
}

export interface FlowRunStepRow {
  step_id: string;
  kind: string;
  result: string;
  at: string | null;
  version_id: number | null;
  replay: boolean;
}

/** One check of a letter decision: `{check: "mailable", holds, why}`, … */
export interface FlowDecisionCheck {
  check: string;
  [key: string]: unknown;
}

export interface FlowDecisionRow {
  id: number;
  run_id: number;
  step_id: string;
  version_id: number | null;
  message_source_id: string | null;
  template_source_id: string | null;
  scheduled_for: string | null;
  decided_at: string | null;
  outcome: FlowDecisionOutcome;
  reason: string;
  /** Due before the flow went into shadow: rebuilt history. */
  replay: boolean;
  detail: {
    checks?: FlowDecisionCheck[];
    message_name?: string | null;
    variant?: string | null;
    partial?: boolean;
    transactional?: boolean;
    [key: string]: unknown;
  };
}

export interface ContactFlowRun extends FlowRunRow {
  events: FlowRunEventRow[];
  steps: FlowRunStepRow[];
  decisions: FlowDecisionRow[];
}

export interface ContactFlowRuns {
  contact_id: number;
  runs: ContactFlowRun[];
}

export function fetchContactFlowRuns(id: number): Promise<ContactFlowRuns> {
  return request<ContactFlowRuns>(`/contacts/${id}/flow-runs`);
}

// --- the flow editor (7.3 M7) ---------------------------------------------------
//
// Admins write; everybody reads. A draft saves whole with the revision it was
// loaded at (409 with `detail.revision` when somebody saved in between); a
// publish with problems is 422 with `detail.problems`; a flow that follows
// Klaviyo needs `stop_following_klaviyo` (409 `follows_klaviyo` without it).

export function createFlow(body: { country: string; name: string }): Promise<FlowDetail> {
  return request<FlowDetail>("/flows/", { method: "POST", body: JSON.stringify(body) });
}

export function startFlowDraft(
  id: number,
  body: { from_number?: number | null; note?: string | null } = {},
): Promise<FlowVersionDetail> {
  return request<FlowVersionDetail>(`/flows/${id}/versions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function saveFlowDraft(
  id: number,
  number: number,
  body: { definition: FlowDefinition; revision: number },
): Promise<FlowVersionDetail> {
  return request<FlowVersionDetail>(`/flows/${id}/versions/${number}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function publishFlowDraft(
  id: number,
  number: number,
  body: { revision: number; migrate_runs: boolean; reason: string; stop_following_klaviyo: boolean },
): Promise<FlowDetail> {
  return request<FlowDetail>(`/flows/${id}/versions/${number}/publish`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function discardFlowDraft(id: number, number: number, reason: string | null): Promise<FlowVersionDetail> {
  return request<FlowVersionDetail>(`/flows/${id}/versions/${number}/discard`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

/** What would keep this unsaved definition from being published. */
export function checkFlowDefinition(definition: FlowDefinition): Promise<{ problems: FlowProblem[] }> {
  return request<{ problems: FlowProblem[] }>("/flows/check", {
    method: "POST",
    body: JSON.stringify({ definition }),
  });
}

export interface FlowFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface FlowStepChange {
  id: string;
  change: "added" | "removed" | "changed";
  kind: string | null;
  fields: FlowFieldChange[];
}

export interface FlowDiff {
  before: number;
  after: number;
  /** Nothing changed but order without meaning (Klaviyo reordering its actions). */
  same: boolean;
  settings: FlowFieldChange[];
  steps: FlowStepChange[];
}

export function fetchFlowDiff(id: number, before: number, after: number): Promise<FlowDiff> {
  return request<FlowDiff>(`/flows/${id}/versions/${before}/diff/${after}`);
}

export interface FlowSource {
  definition_id: number;
  raw: Record<string, unknown>;
  raw_hash: string;
  first_seen_at: string;
  last_seen_at: string;
}

export function fetchFlowSource(id: number, number: number): Promise<FlowSource> {
  return request<FlowSource>(`/flows/${id}/versions/${number}/source`);
}

export interface FlowCatalog {
  country: string;
  metrics: { source_id: string; name: string; integration: string | null }[];
  lists: { source_id: string; name: string | null }[];
  segments: { source_id: string; name: string | null }[];
  templates: { id: number; key: string; name: string; version_id: number | null; language: string | null }[];
  /** Whose name the country's letters go out under, and their footer: from
   * the sending account, never from a flow (rule 5). Null: no account. */
  sender: {
    sender_name: string;
    sender_email: string;
    reply_to: string;
    company_name: string;
    company_address: string;
    unsubscribe_text: string;
  } | null;
}

export function fetchFlowCatalog(country: string): Promise<FlowCatalog> {
  return request<FlowCatalog>(`/flows/catalog?country=${encodeURIComponent(country)}`);
}

/** Per step: how many runs reached it, how many letters it sent (in shadow:
 * would have sent) and skipped. The engine's endpoint (M3, F3); until it is
 * there, the canvas shows no numbers. */
export interface FlowStepStats {
  reached: number;
  sent: number;
  skipped: number;
}

export interface FlowStats {
  version: number | null;
  steps: Record<string, FlowStepStats>;
}

/** The engine's per-step numbers, or null while the endpoint does not exist. */
export async function fetchFlowStats(id: number): Promise<FlowStats | null> {
  try {
    return await request<FlowStats>(`/flows/${id}/stats`);
  } catch (error: unknown) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 405)) return null;
    throw error;
  }
}

// --- the flow messages' templates, imported from Klaviyo (7.2 M5) -----------

/** contact | system | event | unsupported: what the import made of a tag. */
export type FlowTemplateTagKind = "contact" | "system" | "event" | "unsupported";

export interface FlowTemplateUse {
  flow_id: number;
  flow_name: string;
  step_id: string;
  /** "main", or the A/B variant's id. */
  variant: string;
}

export interface FlowTemplateRow {
  country: string;
  template_source_id: string;
  /** imported | failed | missing: how the last pull went. */
  status: string;
  /** ready | needs_conversion: whether our copy could go out as it is. */
  conversion_status: string | null;
  error: string | null;
  name: string | null;
  editor_type: string | null;
  email_template_id: number | null;
  template_key: string | null;
  version: number | null;
  version_id: number | null;
  content_hash: string | null;
  imported_at: string | null;
  last_seen_at: string;
  counts: Partial<Record<FlowTemplateTagKind, number>>;
  event_paths: string[];
  used_by: FlowTemplateUse[];
}

export interface FlowTemplateTag {
  tag: string;
  kind: FlowTemplateTagKind;
  count: number;
  maps_to: string | null;
  paths: string[];
  reason: string | null;
}

export interface FlowTemplateDetail extends FlowTemplateRow {
  tags: FlowTemplateTag[];
  warnings: string[];
  problem: string | null;
  contents: {
    content_hash: string;
    first_seen_at: string;
    last_seen_at: string;
    remote_updated_at: string | null;
    conversion_status: string;
    version: number | null;
    import_error: string | null;
  }[];
}

export interface FlowTemplatePreview {
  html: string;
  text: string;
  subject: string;
  warnings: string[];
  country: string;
  template_source_id: string;
  conversion_status: string | null;
  event_paths: string[];
  email_template_id: number;
  version: number;
}

function flowTemplatePath(country: string, sourceId: string): string {
  return `/flows/templates/${encodeURIComponent(country)}/${encodeURIComponent(sourceId)}`;
}

export function fetchFlowTemplates(country: string): Promise<FlowTemplateRow[]> {
  return request<FlowTemplateRow[]>(`/flows/templates?country=${encodeURIComponent(country)}`);
}

export function fetchFlowTemplate(country: string, sourceId: string): Promise<FlowTemplateDetail> {
  return request<FlowTemplateDetail>(flowTemplatePath(country, sourceId));
}

export function previewFlowTemplate(country: string, sourceId: string): Promise<FlowTemplatePreview> {
  return request<FlowTemplatePreview>(`${flowTemplatePath(country, sourceId)}/preview`);
}

// --- The daily comparison with Klaviyo (7.2 M4) ---------------------------------

/** What one compared item came to. */
export type FlowComparisonOutcome =
  | "match"
  | "time_diff"
  | "klaviyo_only"
  | "emc_only"
  | "out_of_window"
  | "unknown_message";

/** A run of compared days, summed. */
export interface FlowComparisonWindow {
  days: number;
  from: string | null;
  to: string | null;
  outcomes: Record<FlowComparisonOutcome, number>;
  /** Differences the owner's policy does not explain (partial ones apart). */
  unexplained: number;
  explained: number;
  /** Differences decided on history rebuilt as far as the data reached. */
  partial: number;
  /** Differences the facts cannot judge (partial ones included): neither explained nor not. */
  missing_info?: number;
  /** Our sends compared, Klaviyo's receipts compared. */
  sends: number;
  receipts: number;
  out_of_window: number;
  /** Every item but the out-of-window ones. */
  compared: number;
  /** Matches over `compared`; null when nothing was compared. */
  match_rate: number | null;
}

export interface FlowComparisonPending {
  code: string;
  text: string;
  stream?: string;
}

export interface FlowComparisonSummaryRow {
  flow_id: number;
  name: string;
  country: string;
  mode: FlowMode;
  shadow_since: string | null;
  /** By "1", "7", "14": the last that many final days. */
  windows: Record<string, FlowComparisonWindow>;
  last_final_day: string | null;
  days_since_unexplained: number | null;
  never_unexplained: boolean;
  versions: { id: number; number: number | null }[];
  /** May go live: no unexplained difference and no missing info over enough days and sends. */
  ready: boolean;
  ready_detail: {
    days: number;
    days_needed: number;
    sends: number;
    sends_needed: number;
    unexplained: number;
    /** Differences the facts cannot judge, and per kind. */
    missing_info?: number;
    missing_info_kinds?: Record<string, number>;
  };
  /** Today so far (the latest partial day), or null. */
  today: (FlowComparisonWindow & { day: string }) | null;
  pending: { day: string; status: string; reasons: FlowComparisonPending[] }[];
}

export interface FlowComparisonSummary {
  flows: FlowComparisonSummaryRow[];
  ready_days: number;
  ready_min_sends: number;
}

export function fetchFlowComparisonSummary(country = ""): Promise<FlowComparisonSummary> {
  const suffix = country ? `?country=${encodeURIComponent(country)}` : "";
  return request<FlowComparisonSummary>(`/flows/comparison${suffix}`);
}

export interface FlowComparisonReason {
  outcome: FlowComparisonOutcome;
  reason: string;
  label: string;
  explained: boolean;
  partial?: boolean;
  /** The kind of missing info, or null: one reason can be both, each its own line. */
  missing_info?: string | null;
  count: number;
}

export interface FlowComparisonStepDay {
  step_id: string | null;
  outcomes: Record<FlowComparisonOutcome, number>;
  reasons: FlowComparisonReason[];
}

export interface FlowComparisonDayRow {
  day: string;
  status: "final" | "partial" | "pending" | null;
  pending: FlowComparisonPending[];
  counts: Partial<FlowComparisonWindow>;
  version_ids: number[];
  compared_at: string | null;
  steps: FlowComparisonStepDay[];
}

export interface FlowComparisonVersion {
  id: number;
  number: number;
  state: string;
  published_at: string | null;
  created_at: string | null;
  created_by: string;
  note: string | null;
}

export interface FlowComparisonDetail {
  flow_id: number;
  name: string;
  country: string;
  shadow_since: string | null;
  from: string;
  to: string;
  days: FlowComparisonDayRow[];
  reasons: FlowComparisonReason[];
  steps: { step_id: string; name: string | null }[];
  /** Each multi-branch (or random) split's ways over the days: the letters
   * right after each way on both sides, and each side's share (7.4 M5). */
  branches?: FlowComparisonSplit[];
  versions: FlowComparisonVersion[];
  explained_policy: { reason: string; why: string }[];
}

export interface FlowComparisonSplit {
  step_id: string;
  name: string | null;
  /** A way of it is a random sample: the shares must agree, the profiles cannot. */
  random: boolean;
  ways: {
    answer: string;
    name: string | null;
    else: boolean;
    random: boolean;
    letters: string[];
    ours: number;
    klaviyo: number;
    ours_share: number | null;
    klaviyo_share: number | null;
  }[];
}

export function fetchFlowComparison(
  id: number,
  params: { from?: string; to?: string } = {},
): Promise<FlowComparisonDetail> {
  const query = new URLSearchParams();
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  const suffix = query.toString();
  return request<FlowComparisonDetail>(`/flows/${id}/comparison${suffix ? `?${suffix}` : ""}`);
}

export interface FlowComparisonItem {
  id: number;
  day: string;
  step_id: string | null;
  profile_source_id: string | null;
  contact_id: number | null;
  decision_id: number | null;
  klaviyo_event_id: number | null;
  message_source_id: string | null;
  version_id: number | null;
  ours_at: string | null;
  klaviyo_at: string | null;
  delta_seconds: number | null;
  outcome: FlowComparisonOutcome;
  reason: string | null;
  reason_label: string;
  explained: boolean;
  partial: boolean;
  /** The kind of missing info (the facts cannot judge it), or null. */
  missing_info?: string | null;
  missing_info_label?: string;
  detail: Record<string, unknown>;
}

export interface FlowComparisonItemsPage {
  items: FlowComparisonItem[];
  next: number | null;
}

export function fetchFlowComparisonItems(
  id: number,
  params: {
    day?: string;
    outcome?: string;
    reason?: string;
    step?: string;
    differences?: boolean;
    cursor?: number | null;
    limit?: number;
  } = {},
): Promise<FlowComparisonItemsPage> {
  const query = new URLSearchParams();
  if (params.day) query.set("day", params.day);
  if (params.outcome) query.set("outcome", params.outcome);
  if (params.reason) query.set("reason", params.reason);
  if (params.step) query.set("step", params.step);
  if (params.differences) query.set("differences", "true");
  if (params.cursor) query.set("cursor", String(params.cursor));
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return request<FlowComparisonItemsPage>(`/flows/${id}/comparison/items${suffix ? `?${suffix}` : ""}`);
}

/** One point of a profile's two timelines: Klaviyo's (`klaviyo`) or ours (`emc`). */
export interface FlowTimelineItem {
  side: "klaviyo" | "emc";
  kind: "event" | "join" | "entry" | "step" | "decision" | "move";
  at: string | null;
  id?: number;
  run_id?: number;
  metric?: string;
  flow_source_id?: string | null;
  message_source_id?: string | null;
  step_id?: string | null;
  this_flow?: boolean;
  group_id?: string;
  state?: string;
  outcome_reason?: string | null;
  trigger_kind?: string;
  partial?: boolean;
  step_kind?: string;
  result?: string;
  replay?: boolean;
  outcome?: string;
  reason?: string;
  checks?: FlowDecisionCheck[];
  from?: string | null;
  to?: string | null;
  made_at?: string;
  detail?: Record<string, unknown>;
  value_cents?: number | null;
  currency?: string | null;
}

export interface FlowComparisonTimeline {
  flow_id: number;
  flow_external_id: string | null;
  profiles: string[];
  from: string;
  to: string;
  items: FlowTimelineItem[];
  comparisons: FlowComparisonItem[];
}

export function fetchFlowComparisonTimeline(
  id: number,
  who: string,
  params: { around?: string | null; days?: number } = {},
): Promise<FlowComparisonTimeline> {
  const query = new URLSearchParams();
  if (params.around) query.set("around", params.around);
  if (params.days) query.set("days", String(params.days));
  const suffix = query.toString();
  return request<FlowComparisonTimeline>(
    `/flows/${id}/comparison/contact/${encodeURIComponent(who)}${suffix ? `?${suffix}` : ""}`,
  );
}
